import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { followCamera, getWorldSize, isWorldPointVisible, screenToWorld, type CameraState } from "./game/camera";
import { createSoundBoard, type SoundBoard } from "./game/audio";
import { ARENA_EDGE, ARENA_TOP, ELIMINATION_SCORE, findParticipant } from "./game/combat";
import { LANGUAGES, rememberLanguage, resolveLanguage, translator, type Language, type MessageKey } from "./i18n";
import { clamp, createGame, getBotColor, LOCAL_PLAYER_ID, PLAYER_NAME, type GameData, type Leader } from "./game/model";
import { getMinimapViewport, projectToMinimap } from "./game/minimap";
import { getOrbitPosition, orbitPhaseFor } from "./game/orbit";
import { getStormCenter, getStormState, STORM_COLOR, STORM_WARNING_SECONDS } from "./game/storm";
import { calculateRenderScale } from "./game/render-quality";
import { stepGame, type GameEvent, type PlayerInput } from "./game/step";
import { isBounty } from "./game/bounty";
import {
  addFlash,
  addTrauma,
  advanceFeedback,
  createFeedback,
  pushFeed,
  pushNumber,
  pushRing,
  registerKill,
  resetStreak,
  shakeOffset,
  TRAUMA,
  type FeedEntry,
} from "./game/feedback";
import { applySnapshot } from "./net/apply";
import { ArenaClient, defaultArenaUrl, type ConnectionStatus } from "./net/client";
import { ARENA_PALETTES, BALANCE_IS_SERVER_OWNED, DEFAULT_GAME_SETTINGS, THEME_OPTIONS, getCollisionRadius, isPresentationKey, mergeSettings, chargeOf, MAX_ORBIT_SLOTS, normalizeSettings, orbitRingOf, orbitRingRadius, RING_SLOTS, surgeSteal, surgeTier, toBalanceSettings, toPresentationSettings, type GameSettings } from "./game/settings";

/** Builds the offline practice arena the client runs when it is not connected. */
function newLocalGame(width: number, height: number, settings: GameSettings) {
  return createGame({
    width,
    height,
    matchDuration: settings.matchDuration,
    fireInterval: settings.fireInterval,
    seed: Math.floor(Date.now() % 2147483647),
    humans: [{ id: LOCAL_PLAYER_ID, name: PLAYER_NAME, color: settings.playerColor }],
  });
}

/** Ages the decorative spark particles. Online these are the only thing we simulate. */
function advanceLocalSparks(game: GameData, dt: number) {
  for (const spark of game.sparks) {
    if (!spark.active) continue;
    spark.x += spark.vx * dt;
    spark.y += spark.vy * dt;
    spark.vx *= Math.pow(0.08, dt);
    spark.vy *= Math.pow(0.08, dt);
    spark.life -= dt;
    if (spark.life <= 0) spark.active = false;
  }
}

/** The local human always exists in a local game; treat its absence as a bug. */
function requireLocalPlayer(game: GameData) {
  const local = findParticipant(game, LOCAL_PLAYER_ID);
  if (!local) throw new Error("local participant missing from the arena");
  return local;
}

/** Which name a streak has earned. Falls back to the top rung above the last one. */
function streakKey(count: number): MessageKey {
  if (count >= 10) return "streak10";
  if (count >= 7) return "streak7";
  if (count >= 5) return "streak5";
  if (count >= 3) return "streak3";
  return "streak2";
}

const TUTORIAL_STEPS: Array<{ icon: string; title: MessageKey; copy: MessageKey }> = [
  { icon: "✦", title: "tutorialCollect", copy: "tutorialCollectCopy" },
  { icon: "➤", title: "tutorialLaunch", copy: "tutorialLaunchCopy" },
  { icon: "＋", title: "tutorialSteal", copy: "tutorialStealCopy" },
  { icon: "⬡", title: "tutorialShield", copy: "tutorialShieldCopy" },
];

/**
 * Current arcing between two orbs.
 *
 * Deterministic: the kink pattern comes from the pair's index and the clock, not
 * from Math.random, so it flickers the same way on every frame rather than boiling.
 * Two strokes rather than a shadow — a blur on hundreds of short paths per frame is
 * the most expensive thing a canvas can be asked to do, and this reads the same.
 */
function drawArc(
  ctx: CanvasRenderingContext2D,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  seed: number,
  time: number,
) {
  const dx = bx - ax;
  const dy = by - ay;
  const nx = -dy;
  const ny = dx;
  ctx.beginPath();
  ctx.moveTo(ax, ay);
  for (let step = 1; step < ARC_SEGMENTS; step += 1) {
    const along = step / ARC_SEGMENTS;
    // Two waves of different speed so the kink never settles into a rhythm.
    const wobble = Math.sin(time * 17 + seed * 2.3 + step * 2.1) * 0.5
      + Math.sin(time * 29 + seed * 5.7 + step * 3.7) * 0.5;
    const spread = Math.sin(along * Math.PI) * ARC_SPREAD * wobble;
    ctx.lineTo(ax + dx * along + nx * spread, ay + dy * along + ny * spread);
  }
  ctx.lineTo(bx, by);
  ctx.stroke();
}

/** How jagged, how many kinks, and how hard it flickers. All pure taste. */
const ARC_SEGMENTS = 5;
const ARC_SPREAD = 0.17;

/** Off until the phone build is designed; see the block it guards. */
const TOUCH_CONTROLS = false;

/**
 * On trial: arcs of current crackling between the orbs of a ring.
 *
 * Set to false and the orbits go back to exactly what they were — nothing else
 * reads this flag, and no state, protocol or simulation depends on it. It is a
 * drawing, and drawings are cheap to change their mind about.
 */
const ORB_ARCS = true;

/**
 * Frame timings and input lag, on `?debug`. They were how the stutter was found
 * and are worth keeping a keystroke away, but "18/1 · 0ms" means nothing to a
 * player and sits in the middle of the radar panel.
 */
const SHOW_DIAGNOSTICS = typeof window !== "undefined"
  && new URLSearchParams(window.location.search).has("debug");

type Placement = { daily: number; weekly: number | null; allTime: number | null; unranked?: boolean };
type RoomListing = { capacity: number; rooms: Array<{ id: string; number: number; players: number; bots: number }> };

/**
 * `room-1` is the arena's identifier, not a label. It reaches the screen in two
 * places, and in both it should read as a room number in the player's language.
 */
function roomLabel(id: string, t: ReturnType<typeof translator>) {
  const number = /(\d+)\s*$/.exec(id)?.[1];
  return number ? t("roomLabel", { number }) : id.toUpperCase();
}

const NICK_STORAGE_KEY = "orbit.nick";
/** Matches MAX_NAME_LENGTH on the server, which trims anything longer anyway. */
const MAX_NICK_LENGTH = 14;

const SETTINGS_STORAGE_KEY = "n-nova-arena-settings";
/**
 * Balance lives under its own key and is explicitly local-only. Once the client
 * talks to a match server, `BALANCE_IS_SERVER_OWNED` flips and this store is
 * ignored — a player must not be able to grant themselves range or fire rate.
 */
const LOCAL_BALANCE_STORAGE_KEY = "n-nova-arena-balance-local";

/** 60Hz simulation tick — the rate a server would run and clients would reconcile to. */
const FIXED_TIMESTEP = 1 / 60;
/** Never simulate more than this much wall time in one frame. */
const MAX_FRAME_SECONDS = 0.25;

export default function Home() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const minimapRef = useRef<HTMLCanvasElement | null>(null);
  /** Reused each frame: one core's orb positions, so the arc pass allocates nothing. */
  const orbSpotsRef = useRef(new Float32Array(MAX_ORBIT_SLOTS * 2));
  const radarPanelRef = useRef<HTMLElement | null>(null);
  const boardPanelRef = useRef<HTMLElement | null>(null);
  const gameRef = useRef<GameData | null>(null);
  // `active` means the aim point is valid and should be drawn; `steer` means a
  // pointer is currently driving the core. A mouse keeps both while it is on the
  // canvas, a touch drops `steer` on release so the core stops at the last drag.
  const pointerRef = useRef({ x: 0, y: 0, active: false, steer: false });
  const screenPointerRef = useRef({ x: 0, y: 0, active: false, steer: false });
  const steerPointerIdRef = useRef<number | null>(null);
  const attackPointerIdRef = useRef<number | null>(null);
  const boostPointerIdRef = useRef<number | null>(null);
  const attackRequestedRef = useRef(false);
  const boostRequestedRef = useRef(false);
  const shieldRequestedRef = useRef(false);
  const cameraRef = useRef<CameraState>({ x: 0, y: 0 });
  const keysRef = useRef<Record<string, boolean>>({});
  const toastTimerRef = useRef<number | null>(null);
  const tutorialStepRef = useRef<number | null>(null);
  const settingsRef = useRef<GameSettings>({ ...DEFAULT_GAME_SETTINGS });
  const attackHeldRef = useRef(false);
  const boostHeldRef = useRef(false);
  const stationActiveRef = useRef(false);
  const pausedBeforeOverlayRef = useRef(false);
  const pausedRef = useRef(false);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const leaderSignatureRef = useRef("");
  const frameTimesRef = useRef<number[]>([]);
  const loopTimesRef = useRef<number[]>([]);
  const persistTimerRef = useRef<number | null>(null);
  /**
   * Which participant the camera follows and the HUD reports on. Offline that is the
   * fixed local id; online it is whatever identity the arena assigned us.
   */
  const localIdRef = useRef<string>(LOCAL_PLAYER_ID);
  const arenaRef = useRef<ArenaClient | null>(null);
  const onlineRef = useRef(false);
  /** Lets the socket callbacks reach the event consumer defined inside the loop effect. */
  const consumeEventsRef = useRef<((events: GameEvent[]) => void) | null>(null);
  const soundRef = useRef<SoundBoard | null>(null);
  const getSound = () => (soundRef.current ??= createSoundBoard());
  /**
   * Shake, floating numbers, impact rings and the kill feed. A ref rather than
   * state: it changes every frame and only the canvas reads most of it — putting it
   * in React would re-render the page sixty times a second to move a number by four
   * pixels. The two parts that are DOM are published on the UI tick like the rest.
   */
  const feedbackRef = useRef(createFeedback());
  const feedSignatureRef = useRef("");
  const bannerIdRef = useRef(0);
  /** Someone who asked their system for less motion does not get screen shake. */
  const reducedMotionRef = useRef(false);
  /**
   * Orbs taken from the current storm, counted from the events rather than sent.
   * The server owns the real figure, but it is per-player state and putting it on
   * every player in every snapshot would cost twenty times what one client needs.
   * Counting our own `storm-orb` events gives the same number over a reliable
   * socket, and it resets when the next storm announces itself.
   */
  const stormTakenRef = useRef(0);

  const [started, setStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [language, setLanguage] = useState<Language>(() => resolveLanguage());
  /** The opening line is shown before anything happens, so it follows the language. */
  const [openingToast, setOpeningToast] = useState(true);
  const t = useMemo(() => translator(language), [language]);
  /** Thousands separators differ: 1.284 in Turkish, 1,284 in English. */
  const locale = language === "tr" ? "tr-TR" : "en-GB";
  /** Toasts are raised from the game loop, which must not be rebuilt per render. */
  const tRef = useRef(t);
  /** Who the arena will call you. Null until /auth/me answers. */
  const [identity, setIdentity] = useState<{ name: string } | null>(null);
  const [googleReady, setGoogleReady] = useState(false);
  const [nick, setNick] = useState(() => {
    try { return window.localStorage.getItem(NICK_STORAGE_KEY) ?? ""; } catch { return ""; }
  });
  const [signInError, setSignInError] = useState<MessageKey | "">(() =>
    new URLSearchParams(window.location.search).has("signin") ? "signInFailed" : "");
  /** The core the arena gave us, for highlighting our own row. */
  const [localId, setLocalId] = useState<string>(LOCAL_PLAYER_ID);
  /** Where the last life placed, as the arena counted it. */
  const [placed, setPlaced] = useState<Placement | null>(null);
  const [rooms, setRooms] = useState<RoomListing | null>(null);
  const [roomsOpen, setRoomsOpen] = useState(false);
  const identityRef = useRef<{ name: string } | null>(null);
  const nickRef = useRef(nick);
  useEffect(() => { identityRef.current = identity; }, [identity]);
  useEffect(() => { nickRef.current = nick; }, [nick]);
  const [paused, setPaused] = useState(false);
  const [score, setScore] = useState(0);
  const [orbs, setOrbs] = useState(8);
  const [timeLeft, setTimeLeft] = useState(DEFAULT_GAME_SETTINGS.matchDuration);
  /** An arena the operator set to run without a clock: nothing to count down. */
  const [endless, setEndless] = useState(false);
  /**
   * Your own surge, and only yours. Everyone can see an opponent's rings light up —
   * that is the warning the effect exists to give — but how long until yours comes
   * back is your business and nobody else's.
   */
  const [surge, setSurge] = useState({ tier: 0, wait: 0 });
  const [shieldCooldown, setShieldCooldown] = useState(0);
  const [boostRemaining, setBoostRemaining] = useState(0);
  const [leaders, setLeaders] = useState<Leader[]>([]);
  const [rank, setRank] = useState(20);
  const [toast, setToast] = useState("");
  /** Recent kills anywhere in the room, newest first. */
  const [feed, setFeed] = useState<FeedEntry[]>([]);
  /** The streak the local player is on, when it has reached a milestone worth saying. */
  const [streak, setStreak] = useState<{ id: number; count: number } | null>(null);
  /** Who the room is hunting. Null when nobody is far enough ahead to be marked. */
  const [hunted, setHunted] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [muted, setMuted] = useState(false);
  const [tutorialStep, setTutorialStep] = useState<number | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState<GameSettings>({ ...DEFAULT_GAME_SETTINGS });
  const [online, setOnline] = useState(false);
  const [netStatus, setNetStatus] = useState<ConnectionStatus>("idle");
  const [roomName, setRoomName] = useState<string | null>(null);
  const [playerCount, setPlayerCount] = useState(0);
  const [fps, setFps] = useState(0);
  const [ping, setPing] = useState(0);
  const [inputLag, setInputLag] = useState(0);
  const [worstFrame, setWorstFrame] = useState(0);
  const [worstLoop, setWorstLoop] = useState(0);

  useEffect(() => {
    const read = (key: string) => {
      try {
        const saved = window.localStorage.getItem(key);
        return saved ? JSON.parse(saved) : null;
      } catch {
        // A corrupt or unreadable store must never break startup; removing it can
        // throw too (private mode), so that attempt is guarded separately.
        try { window.localStorage.removeItem(key); } catch { /* ignore */ }
        return null;
      }
    };

    const presentation = read(SETTINGS_STORAGE_KEY);
    // Balance is only ever restored while the client still owns it.
    const balance = BALANCE_IS_SERVER_OWNED ? null : read(LOCAL_BALANCE_STORAGE_KEY);
    if (!presentation && !balance) return;
    // Goes through mergeSettings for the same reason the network path will: the
    // presentation store is client-writable, so it may only supply presentation keys.
    const restored = mergeSettings(
      toBalanceSettings(normalizeSettings({
        ...DEFAULT_GAME_SETTINGS,
        ...(balance && typeof balance === "object" ? balance : {}),
      })),
      (presentation && typeof presentation === "object" ? presentation : {}) as Partial<GameSettings>,
    );
    settingsRef.current = restored;
    // Hydrating from localStorage is exactly "subscribe to an external store after
    // mount": the server cannot know the stored value, so it must land in a
    // post-mount render. Runs once, with no dependency that can retrigger it.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSettings(restored);
  }, []);

  const applySettings = (next: GameSettings) => {
    settingsRef.current = next;
    setSettings(next);
    // Dragging a slider fires this continuously; a synchronous stringify + write on
    // every event janks the frame, so the persist is coalesced.
    if (persistTimerRef.current) window.clearTimeout(persistTimerRef.current);
    persistTimerRef.current = window.setTimeout(() => {
      try {
        window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(toPresentationSettings(next)));
        if (!BALANCE_IS_SERVER_OWNED) {
          window.localStorage.setItem(LOCAL_BALANCE_STORAGE_KEY, JSON.stringify(toBalanceSettings(next)));
        }
      } catch { /* ignore */ }
    }, 250);
    const game = gameRef.current;
    if (game) {
      let botIndex = 0;
      for (const participant of game.participants) {
        if (participant.id === localIdRef.current) participant.color = next.playerColor;
        else if (participant.kind === "bot") participant.color = getBotColor(botIndex++, next.playerColor);
        participant.orbs = Math.min(next.maxOrbs, participant.orbs);
      }
      // Resolve by identity, not by array position: once real players join and
      // leave, an owner id no longer maps to an index.
      game.energy.forEach((item) => {
        item.color = findParticipant(game, item.owner)?.color ?? item.color;
      });
    }
  };

  const updateSetting = <Key extends keyof GameSettings>(key: Key, value: GameSettings[Key]) => {
    // Balance edits are refused outright once the server owns them, rather than
    // being accepted locally and silently diverging from the authoritative match.
    if (BALANCE_IS_SERVER_OWNED && !isPresentationKey(key)) return;
    applySettings(normalizeSettings({ ...settingsRef.current, [key]: value }));
  };

  const syncAimFromScreen = useCallback(() => {
    const screenAim = screenPointerRef.current;
    pointerRef.current = {
      ...screenToWorld(screenAim.x, screenAim.y, cameraRef.current),
      active: screenAim.active,
      steer: screenAim.steer,
    };
  }, []);

  const releaseAllInputs = useCallback(() => {
    keysRef.current = {};
    attackHeldRef.current = false;
    boostHeldRef.current = false;
    attackPointerIdRef.current = null;
    boostPointerIdRef.current = null;
    steerPointerIdRef.current = null;
    screenPointerRef.current = { ...screenPointerRef.current, steer: false };
    syncAimFromScreen();
  }, [syncAimFromScreen]);

  // Below the state it touches: an effect that reaches a setter declared later
  // cannot be treated as stable, and the compiler stops optimising the component.
  useEffect(() => {
    tRef.current = t;
    rememberLanguage(language);
  }, [t, language]);

  // Screen shake is the one thing here that can make someone ill. The system setting
  // is honoured live, so turning it on does not require reloading the game.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => { reducedMotionRef.current = query.matches; };
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);

  // Derived rather than stored: writing the opening line into state from an effect
  // is a render that causes another render, and it would go stale on a language
  // change anyway.
  const shownToast = toast || (openingToast ? t("firstToast") : "");

  const showToast = useCallback((text: string) => {
    // Anything real replaces the opening line, which then stops re-appearing on a
    // language change.
    setOpeningToast(false);
    setToast(text);
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(""), 1700);
  }, []);

  // Overlays pause the match while they are open, but closing one must restore the
  // pause the player had chosen, not blindly resume.
  const openOverlay = useCallback((overlay: "help" | "settings") => {
    pausedBeforeOverlayRef.current = pausedRef.current;
    releaseAllInputs();
    setPaused(true);
    if (overlay === "help") setHelpOpen(true);
    else setSettingsOpen(true);
  }, [releaseAllInputs]);

  const closeOverlay = useCallback(() => {
    setHelpOpen(false);
    setSettingsOpen(false);
    setPaused(pausedBeforeOverlayRef.current);
  }, []);

  const advanceTutorial = useCallback((completedStep: number) => {
    if (tutorialStepRef.current !== completedStep) return;
    const nextStep = completedStep + 1;
    if (nextStep >= TUTORIAL_STEPS.length) {
      tutorialStepRef.current = null;
      setTutorialStep(null);
      showToast(tRef.current("tutorialDone"));
      return;
    }
    tutorialStepRef.current = nextStep;
    setTutorialStep(nextStep);
  }, [showToast]);

  const resetGame = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const world = getWorldSize(rect);
    gameRef.current = newLocalGame(world.width, world.height, settingsRef.current);
    resetStreak(feedbackRef.current);
    const localPlayer = requireLocalPlayer(gameRef.current);
    followCamera(cameraRef.current, localPlayer.x, localPlayer.y, world, rect, 0, true);
    screenPointerRef.current = { x: rect.width * 0.5, y: rect.height * 0.42, active: false, steer: false };
    steerPointerIdRef.current = null;
    attackPointerIdRef.current = null;
    boostPointerIdRef.current = null;
    attackHeldRef.current = false;
    boostHeldRef.current = false;
    stationActiveRef.current = false;
    leaderSignatureRef.current = "";
    syncAimFromScreen();
    setScore(0);
    setOrbs(8);
    setRank(20);
    setTimeLeft(settingsRef.current.matchDuration);
    setShieldCooldown(0);
    setBoostRemaining(0);
    setGameOver(false);
    setPaused(false);
    setStarted(true);
    tutorialStepRef.current = 0;
    setTutorialStep(0);
    // Started from a click, which is the only moment browsers let audio begin.
    getSound().unlock();
    showToast(tRef.current("toastReady"));
  }, [showToast, syncAimFromScreen]);

  // Actions are latched rather than executed on the spot: the simulation consumes
  // them on its next fixed tick, which is what a server would receive over the wire.
  // A latch also survives a tap shorter than one tick.
  const requestAttack = useCallback(() => { attackRequestedRef.current = true; }, []);
  const requestBoost = useCallback(() => { boostRequestedRef.current = true; }, []);
  const requestShield = useCallback(() => { shieldRequestedRef.current = true; }, []);

  useEffect(() => {
    // The failure marker has been read into state already; drop it from the bar so a
    // refresh does not resurrect the message.
    if (new URLSearchParams(window.location.search).has("signin")) {
      window.history.replaceState(null, "", window.location.pathname);
    }
    let cancelled = false;
    fetch("/auth/me", { credentials: "same-origin" })
      .then((response) => response.json())
      .then((body) => {
        if (cancelled) return;
        setGoogleReady(Boolean(body.google));
        setIdentity(body.identity ?? null);
      })
      // Offline or an old build: guests still work, so this is not worth a message.
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const startOnline = useCallback((room: string | null = null) => {
    if (arenaRef.current) return;
    if (!identityRef.current && !nickRef.current.trim()) {
      setSignInError("nickRequired");
      return;
    }
    setSignInError("");
    setPlaced(null);
    if (!identityRef.current) {
      try { window.localStorage.setItem(NICK_STORAGE_KEY, nickRef.current.trim()); } catch { /* private mode */ }
    }

    const client = new ArenaClient({
      url: defaultArenaUrl(window.location),
      name: identityRef.current?.name ?? nickRef.current,
      room,
      onStatus: setNetStatus,
      onJoined: (info) => {
        localIdRef.current = info.you;
        setLocalId(info.you);
        // Adopt the arena's balance so the client draws the same hitboxes and range
        // indicator the server is actually enforcing.
        settingsRef.current = normalizeSettings({ ...settingsRef.current, ...info.settings });
        setSettings(settingsRef.current);
        const game = gameRef.current;
        if (game) {
          game.width = info.width;
          game.height = info.height;
          game.matchDuration = settingsRef.current.matchDuration;
        }
        setRoomName(info.room);
        showToast(tRef.current("joinedRoom", { room: roomLabel(info.room, tRef.current) }));
      },
      onEvents: (events) => consumeEventsRef.current?.(events as GameEvent[]),
      onRanked: (rank) => setPlaced(rank),
      onMatchOver: () => showToast(tRef.current("toastMatchOver")),
    });

    // The arena writes into an existing GameData, so one must exist before the first
    // snapshot lands — including on a second run after leaving.
    if (!gameRef.current) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const world = getWorldSize(rect);
      gameRef.current = newLocalGame(world.width, world.height, settingsRef.current);
    }
    gameRef.current.running = true;
    gameRef.current.lastTime = performance.now();

    arenaRef.current = client;
    onlineRef.current = true;
    setOnline(true);
    setGameOver(false);
    setPaused(false);
    setStarted(true);
    releaseAllInputs();
    getSound().unlock();
    client.connect();
  }, [releaseAllInputs, showToast]);

  /**
   * Back into the arena without passing through the intro. The socket is dropped
   * and reopened rather than asking the server to revive the core in place: that
   * frees the seat first, so the registry seats you wherever there is room — the
   * same room if it still has space, the next one if twenty people got there
   * first.
   */
  const continueOnline = useCallback(() => {
    arenaRef.current?.disconnect();
    arenaRef.current = null;
    // A streak belongs to a life. Rejoining starts a new one, so the count starts
    // again even though the same person is at the keyboard.
    resetStreak(feedbackRef.current);
    localIdRef.current = LOCAL_PLAYER_ID;
    setLocalId(LOCAL_PLAYER_ID);
    setRoomName(null);
    releaseAllInputs();
    startOnline();
  }, [releaseAllInputs, startOnline]);

  const leaveOnline = useCallback(() => {
    arenaRef.current?.disconnect();
    arenaRef.current = null;
    resetStreak(feedbackRef.current);
    onlineRef.current = false;
    localIdRef.current = LOCAL_PLAYER_ID;
    // Hand the renderer an idle arena rather than nothing. Nulling it froze the
    // canvas: the frame loop bails on a missing game, and only `resize` ever built
    // one, so pressing play again could never reach the online path.
    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      const world = getWorldSize(rect);
      gameRef.current = newLocalGame(world.width, world.height, settingsRef.current);
      gameRef.current.running = false;
    }
    releaseAllInputs();
    setOnline(false);
    setNetStatus("idle");
    setRoomName(null);
    setStarted(false);
    setGameOver(false);
  }, [releaseAllInputs]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const backgroundCanvas = document.createElement("canvas");
    const backgroundCtx = backgroundCanvas.getContext("2d");
    if (!backgroundCtx) return;
    let renderScale = 1;
    let backgroundTheme = "";
    let viewportWidth = 1;
    let viewportHeight = 1;

    const renderStaticBackground = (width: number, height: number) => {
      backgroundCanvas.width = canvas.width;
      backgroundCanvas.height = canvas.height;
      backgroundCtx.setTransform(renderScale, 0, 0, renderScale, 0, 0);
      const palette = ARENA_PALETTES[settingsRef.current.theme];
      const background = backgroundCtx.createRadialGradient(width * 0.5, height * 0.45, 20, width * 0.5, height * 0.5, Math.max(width, height));
      background.addColorStop(0, palette.center);
      background.addColorStop(0.45, palette.middle);
      background.addColorStop(1, palette.edge);
      backgroundCtx.fillStyle = background;
      backgroundCtx.fillRect(0, 0, width, height);

      backgroundTheme = settingsRef.current.theme;
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      viewportWidth = rect.width;
      viewportHeight = rect.height;
      const previousScale = renderScale;
      renderScale = calculateRenderScale(rect.width, rect.height, window.devicePixelRatio || 1);
      // Sprites are baked at the render scale, so they must be rebuilt when it moves.
      if (previousScale !== renderScale) {
        glowSprites.clear();
        coreSprites.clear();
        labelSprites.clear();
        gridPattern = null;
        stationSprites.clear();
      }
      canvas.width = Math.floor(rect.width * renderScale);
      canvas.height = Math.floor(rect.height * renderScale);
      ctx.setTransform(renderScale, 0, 0, renderScale, 0, 0);
      renderStaticBackground(rect.width, rect.height);
      if (!gameRef.current) {
        const world = getWorldSize(rect);
        gameRef.current = newLocalGame(world.width, world.height, settingsRef.current);
        gameRef.current.running = false;
        const idlePlayer = requireLocalPlayer(gameRef.current);
        followCamera(cameraRef.current, idlePlayer.x, idlePlayer.y, gameRef.current, rect, 0, true);
      } else {
        const game = gameRef.current;
        const minimumWorld = getWorldSize(rect);
        game.width = Math.max(game.width, minimumWorld.width);
        game.height = Math.max(game.height, minimumWorld.height);
        const local = requireLocalPlayer(game);
        followCamera(cameraRef.current, local.x, local.y, game, rect, 0, true);
      }
    };

    // The hex lattice repeats every two columns and one row, so it is baked once
    // into a tile and filled as a pattern. Stroking ~700 hexes into a path on every
    // frame was the single most expensive thing left in the draw loop.
    const HEX_SIZE = 34;
    const HEX_COLUMN = HEX_SIZE * 1.5;
    const HEX_ROW = Math.sqrt(3) * HEX_SIZE;
    let gridPattern: CanvasPattern | null = null;
    let gridPatternKey = "";

    const getGridPattern = () => {
      const palette = ARENA_PALETTES[settingsRef.current.theme];
      const key = `${palette.grid}|${renderScale}`;
      if (gridPattern && gridPatternKey === key) return gridPattern;

      const tileWidth = HEX_COLUMN * 2;
      const tileHeight = HEX_ROW;
      const tile = document.createElement("canvas");
      tile.width = Math.max(1, Math.round(tileWidth * renderScale));
      tile.height = Math.max(1, Math.round(tileHeight * renderScale));
      const tileCtx = tile.getContext("2d");
      if (!tileCtx) return null;
      tileCtx.setTransform(renderScale, 0, 0, renderScale, 0, 0);
      tileCtx.globalAlpha = 0.13;
      tileCtx.strokeStyle = palette.grid;
      tileCtx.lineWidth = 0.7;
      tileCtx.beginPath();
      // Draw a 4x3 neighbourhood so hexes crossing the tile edge close correctly.
      for (let row = -1; row <= 1; row += 1) {
        for (let col = -1; col <= 2; col += 1) {
          const cx = col * HEX_COLUMN;
          const cy = row * HEX_ROW + (Math.abs(col) % 2 ? HEX_ROW * 0.5 : 0);
          for (let side = 0; side < 6; side += 1) {
            const angle = (Math.PI / 3) * side;
            const px = cx + Math.cos(angle) * HEX_SIZE;
            const py = cy + Math.sin(angle) * HEX_SIZE;
            if (side === 0) tileCtx.moveTo(px, py);
            else tileCtx.lineTo(px, py);
          }
          tileCtx.closePath();
        }
      }
      tileCtx.stroke();

      gridPattern = ctx.createPattern(tile, "repeat");
      gridPatternKey = key;
      if (gridPattern) {
        gridPattern.setTransform(new DOMMatrix([1 / renderScale, 0, 0, 1 / renderScale, 0, 0]));
      }
      return gridPattern;
    };

    // Four stations x (gradient + glyph) every frame, for something that only has
    // two appearances.
    const stationSprites = new Map<string, HTMLCanvasElement>();

    const getStationSprite = (active: boolean) => {
      const key = active ? "on" : "off";
      const cached = stationSprites.get(key);
      if (cached) return cached;
      const size = 84;
      const canvas = document.createElement("canvas");
      canvas.width = Math.ceil(size * renderScale);
      canvas.height = Math.ceil(size * renderScale);
      const spriteCtx = canvas.getContext("2d");
      if (spriteCtx) {
        spriteCtx.setTransform(renderScale, 0, 0, renderScale, 0, 0);
        const gradient = spriteCtx.createRadialGradient(42, 42, 2, 42, 42, 42);
        gradient.addColorStop(0, active ? "#fffbd1" : "#5a4510");
        gradient.addColorStop(0.28, active ? STORM_COLOR : "#7a5c12");
        gradient.addColorStop(1, "rgba(255,196,35,0)");
        spriteCtx.fillStyle = gradient;
        spriteCtx.beginPath();
        spriteCtx.arc(42, 42, 42, 0, Math.PI * 2);
        spriteCtx.fill();
        spriteCtx.fillStyle = active ? "#fffef1" : "rgba(255,224,110,.75)";
        spriteCtx.font = "900 18px Arial";
        spriteCtx.textAlign = "center";
        spriteCtx.textBaseline = "middle";
        spriteCtx.fillText("\u26a1", 42, 42);
      }
      stationSprites.set(key, canvas);
      return canvas;
    };

    const drawWorldGrid = (camera: CameraState) => {
      const pattern = getGridPattern();
      if (!pattern) return;
      ctx.save();
      ctx.fillStyle = pattern;
      ctx.fillRect(camera.x, camera.y, viewportWidth, viewportHeight);
      ctx.restore();
    };

    // Canvas shadowBlur is a per-draw gaussian, and this scene issues hundreds of
    // glowing circles per frame. Each colour is rendered once into a sprite (still
    // using shadowBlur, so the look is unchanged) and then blitted at any size.
    const GLOW_CORE_RADIUS = 16;
    const GLOW_SPRITE_HALF = 38;
    const glowSprites = new Map<string, HTMLCanvasElement>();

    const getGlowSprite = (color: string) => {
      const cached = glowSprites.get(color);
      if (cached) return cached;
      const sprite = document.createElement("canvas");
      const size = GLOW_SPRITE_HALF * 2;
      sprite.width = Math.ceil(size * renderScale);
      sprite.height = Math.ceil(size * renderScale);
      const spriteCtx = sprite.getContext("2d");
      if (spriteCtx) {
        spriteCtx.setTransform(renderScale, 0, 0, renderScale, 0, 0);
        spriteCtx.shadowColor = color;
        spriteCtx.shadowBlur = GLOW_SPRITE_HALF - GLOW_CORE_RADIUS;
        spriteCtx.fillStyle = color;
        spriteCtx.beginPath();
        spriteCtx.arc(GLOW_SPRITE_HALF, GLOW_SPRITE_HALF, GLOW_CORE_RADIUS, 0, Math.PI * 2);
        spriteCtx.fill();
      }
      glowSprites.set(color, sprite);
      return sprite;
    };

    /**
     * Arcs around one core: each orb to the next on its ring, and the ring closed.
     * Only neighbours, so the cost is one short path per orb rather than one per
     * pair, and it still reads as a cage of current rather than a web.
     */
    const drawRingArcs = (
      ctx: CanvasRenderingContext2D,
      spots: Float32Array,
      count: number,
      color: string,
      time: number,
      blink: number,
      surge: number,
    ) => {
      ctx.save();
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      for (let index = 0; index < count; index += 1) {
        const ring = orbitRingOf(index);
        const first = ring * RING_SLOTS;
        const onThisRing = Math.min(RING_SLOTS, count - first);
        const isLast = index === first + onThisRing - 1;
        // A full ring closes onto its own first orb. A partial one is a chain and
        // stops: joining its last orb back to its first draws a chord straight
        // across the middle, which is a second arc where the eye expects one.
        if (isLast && onThisRing < RING_SLOTS) continue;
        const partner = isLast ? first : index + 1;
        if (partner === index) continue;

        // Each gap has its own rhythm, and a gap dark for a moment is what makes the
        // rest read as current rather than as a wire.
        // Every gap carries current and none cut out — the ring is closed, and it
        // should look closed. A higher ring simply runs harder.
        const pulse = 0.72 + 0.28 * Math.sin(time * (18 + surge * 8) + index * 1.3);
        const strength = Math.min(1, pulse) * blink;

        const ax = spots[index * 2];
        const ay = spots[index * 2 + 1];
        const bx = spots[partner * 2];
        const by = spots[partner * 2 + 1];

        // A wide dim stroke under a narrow bright one: the glow without the blur.
        // Thicker and brighter the deeper the ring, so which tier is armed can be
        // read across the arena rather than counted.
        ctx.lineWidth = 4.5 + surge * 1.9;
        ctx.strokeStyle = colorWithAlpha(color, (0.22 + surge * 0.09) * strength);
        drawArc(ctx, ax, ay, bx, by, index, time);
        ctx.lineWidth = 1.2 + surge * 0.5;
        ctx.strokeStyle = `rgba(240,252,255,${0.92 * strength})`;
        drawArc(ctx, ax, ay, bx, by, index, time);
      }
      ctx.restore();
    };

    const drawGlowCircle = (x: number, y: number, radius: number, color: string) => {
      const sprite = getGlowSprite(color);
      const half = (radius / GLOW_CORE_RADIUS) * GLOW_SPRITE_HALF;
      ctx.drawImage(sprite, x - half, y - half, half * 2, half * 2);
    };

    // The core disc is a radial gradient under a shadowBlur, and it was rebuilt for
    // every core on every frame — a dozen blur passes and a dozen gradient objects
    // 60 times a second. Nothing about it changes but its position, so it is baked
    // per colour and blitted.
    const coreSprites = new Map<string, { canvas: HTMLCanvasElement; half: number }>();

    const getCoreSprite = (color: string, radius: number, isPlayer: boolean) => {
      const key = `${color}|${radius.toFixed(1)}|${isPlayer ? 1 : 0}`;
      const cached = coreSprites.get(key);
      if (cached) return cached;

      const glow = isPlayer ? 35 : 22;
      const half = radius + glow + 4;
      const canvas = document.createElement("canvas");
      canvas.width = Math.ceil(half * 2 * renderScale);
      canvas.height = Math.ceil(half * 2 * renderScale);
      const spriteCtx = canvas.getContext("2d");
      if (spriteCtx) {
        spriteCtx.setTransform(renderScale, 0, 0, renderScale, 0, 0);
        spriteCtx.shadowColor = color;
        spriteCtx.shadowBlur = glow;
        const gradient = spriteCtx.createRadialGradient(half - radius * 0.25, half - radius * 0.25, 1, half, half, radius);
        gradient.addColorStop(0, "#ffffff");
        gradient.addColorStop(0.24, color);
        gradient.addColorStop(1, "rgba(5,17,52,.96)");
        spriteCtx.fillStyle = gradient;
        spriteCtx.strokeStyle = "rgba(255,255,255,.78)";
        spriteCtx.lineWidth = isPlayer ? 2.5 : 1.5;
        spriteCtx.beginPath();
        spriteCtx.arc(half, half, radius, 0, Math.PI * 2);
        spriteCtx.fill();
        spriteCtx.stroke();
        spriteCtx.shadowBlur = 0;
        spriteCtx.fillStyle = "rgba(255,255,255,.94)";
        spriteCtx.font = `800 ${isPlayer ? 17 : 11}px Arial`;
        spriteCtx.textAlign = "center";
        spriteCtx.textBaseline = "middle";
        spriteCtx.fillText("\u2726", half, half + 0.5);
      }
      const sprite = { canvas, half };
      coreSprites.set(key, sprite);
      return sprite;
    };

    // Same story for the name plate: shadowed text, redrawn per core per frame.
    const labelSprites = new Map<string, { canvas: HTMLCanvasElement; width: number; height: number }>();

    const getLabelSprite = (text: string, isPlayer: boolean) => {
      const key = `${text}|${isPlayer ? 1 : 0}`;
      const cached = labelSprites.get(key);
      if (cached) return cached;

      const fontSize = isPlayer ? 11 : 9;
      const measure = document.createElement("canvas").getContext("2d");
      let width = text.length * fontSize * 0.7;
      if (measure) {
        measure.font = `800 ${fontSize}px Arial`;
        width = measure.measureText(text).width;
      }
      const padding = 8;
      const boxWidth = Math.ceil(width + padding * 2);
      const boxHeight = Math.ceil(fontSize + padding * 2);
      const canvas = document.createElement("canvas");
      canvas.width = Math.ceil(boxWidth * renderScale);
      canvas.height = Math.ceil(boxHeight * renderScale);
      const spriteCtx = canvas.getContext("2d");
      if (spriteCtx) {
        spriteCtx.setTransform(renderScale, 0, 0, renderScale, 0, 0);
        spriteCtx.font = `800 ${fontSize}px Arial`;
        spriteCtx.textAlign = "center";
        spriteCtx.textBaseline = "middle";
        spriteCtx.shadowColor = "rgba(0,0,0,.8)";
        spriteCtx.shadowBlur = 5;
        spriteCtx.fillStyle = "rgba(255,255,255,.9)";
        spriteCtx.fillText(text, boxWidth / 2, boxHeight / 2);
      }
      const sprite = { canvas, width: boxWidth, height: boxHeight };
      labelSprites.set(key, sprite);
      return sprite;
    };

    const colorWithAlpha = (color: string, alpha: number) => {
      const value = color.replace("#", "");
      const red = Number.parseInt(value.slice(0, 2), 16);
      const green = Number.parseInt(value.slice(2, 4), 16);
      const blue = Number.parseInt(value.slice(4, 6), 16);
      return `rgba(${red},${green},${blue},${alpha})`;
    };

    const drawSimpleBoostTrail = (x: number, y: number, nx: number, ny: number, color: string, trailLength: number) => {
      const startX = x - nx * trailLength;
      const startY = y - ny * trailLength;
      const endX = x - nx * 24;
      const endY = y - ny * 24;
      const px = -ny;
      const py = nx;
      ctx.save();
      const trail = ctx.createLinearGradient(startX, startY, endX, endY);
      trail.addColorStop(0, colorWithAlpha(color, 0));
      trail.addColorStop(0.65, colorWithAlpha(color, 0.16));
      trail.addColorStop(1, colorWithAlpha(color, 0.52));
      ctx.fillStyle = trail;
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX + px * 7, endY + py * 7);
      ctx.lineTo(endX - px * 7, endY - py * 7);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    };

    const drawBoostTrail = (x: number, y: number, vx: number, vy: number, color: string, time: number, enhanced: boolean) => {
      const speed = Math.hypot(vx, vy);
      if (speed < 8) return;
      const nx = vx / speed;
      const ny = vy / speed;
      const trailLength = 62 + Math.sin(time * 14) * 5;
      if (!enhanced) {
        drawSimpleBoostTrail(x, y, nx, ny, color, trailLength);
        return;
      }
      const startX = x - nx * trailLength;
      const startY = y - ny * trailLength;
      const endX = x - nx * 24;
      const endY = y - ny * 24;
      const px = -ny;
      const py = nx;
      const width = 9 + Math.sin(time * 18) * 1.5;
      ctx.save();
      const flame = ctx.createLinearGradient(startX, startY, endX, endY);
      flame.addColorStop(0, colorWithAlpha(color, 0));
      flame.addColorStop(0.55, colorWithAlpha(color, 0.24));
      flame.addColorStop(1, colorWithAlpha(color, 0.68));
      ctx.fillStyle = flame;
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX + px * width, endY + py * width);
      ctx.lineTo(endX - px * width, endY - py * width);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    };

    const drawCore = (
      x: number,
      y: number,
      color: string,
      count: number,
      time: number,
      isPlayer = false,
      shield = false,
      name = "",
      protectedCore = false,
      surge = 0,
      hunted = false,
    ) => {
      // How many rings this core is actually using, so an empty one is not drawn.
      const ringCount = count > 0 ? orbitRingOf(count - 1) + 1 : 1;
      const visuals = settingsRef.current;
      const base = visuals.coreRadius;
      const outerRadius = orbitRingRadius(ringCount - 1, visuals);

      // While immune the whole core flashes. A halo alone was too easy to miss, and
      // shots visibly passing through an apparently solid core reads as a bug unless
      // the core itself says it is not there yet.
      const blink = protectedCore ? 0.3 + 0.7 * (0.5 + 0.5 * Math.sin(time * 20)) : 1;

      ctx.save();
      ctx.globalAlpha = 0.44 * blink;
      ctx.strokeStyle = color;
      ctx.lineWidth = isPlayer ? 1.8 : 1.2;
      ctx.setLineDash([3, 5]);
      for (let ring = 0; ring < ringCount; ring += 1) {
        ctx.beginPath();
        ctx.arc(x, y, orbitRingRadius(ring, visuals), 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.restore();

      ctx.save();
      ctx.globalAlpha = blink;

      // Positions first: the arcs are drawn under the spheres, so the light appears
      // to come from between them rather than to be painted over them.
      const orbSpots = orbSpotsRef.current;
      for (let index = 0; index < count; index += 1) {
        const position = getOrbitPosition(x, y, index, time, visuals);
        orbSpots[index * 2] = position.x;
        orbSpots[index * 2 + 1] = position.y;
      }

      if (ORB_ARCS && surge > 0) drawRingArcs(ctx, orbSpots, count, color, time, blink, surge);

      for (let index = 0; index < count; index += 1) {
        const ox = orbSpots[index * 2];
        const oy = orbSpots[index * 2 + 1];
        drawGlowCircle(ox, oy, visuals.orbRadius, color);
        ctx.fillStyle = `rgba(255,255,255,${0.82 * blink})`;
        ctx.beginPath();
        ctx.arc(ox - 1.5, oy - 1.5, Math.max(1.7, visuals.orbRadius * 0.3), 0, Math.PI * 2);
        ctx.fill();
      }

      const core = getCoreSprite(color, base, isPlayer);
      ctx.drawImage(core.canvas, x - core.half, y - core.half, core.half * 2, core.half * 2);
      ctx.restore();

      if (protectedCore) {
        // Distinct from the shield: a slow rotating halo, so it reads as "cannot be
        // hit yet" rather than "about to catch a shot".
        ctx.save();
        ctx.globalAlpha = 0.5 + Math.sin(time * 4) * 0.18;
        ctx.strokeStyle = "rgba(255,255,255,.85)";
        ctx.lineWidth = 2;
        ctx.setLineDash([10, 9]);
        ctx.lineDashOffset = -time * 26;
        ctx.beginPath();
        ctx.arc(x, y, visuals.thirdOrbitRadius + visuals.orbRadius + 26, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }

      if (shield) {
        // The player's own colour, not a fixed cyan: with twenty cores in an arena,
        // a shield has to say *whose* it is at a glance.
        ctx.save();
        ctx.strokeStyle = colorWithAlpha(color, 0.9);
        ctx.lineWidth = 5;
        ctx.shadowColor = color;
        ctx.shadowBlur = 28;
        ctx.beginPath();
        ctx.arc(x, y, visuals.thirdOrbitRadius + visuals.orbRadius + 16 + Math.sin(time * 5) * 2, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }

      if (hunted) {
        // A gold ring outside everything else, turning steadily, plus a crown over
        // the name. Two marks rather than one: colour alone is not enough to pick a
        // core out of a crowded arena, and motion is what the eye actually catches.
        ctx.save();
        ctx.strokeStyle = "rgba(255,201,74,.92)";
        ctx.lineWidth = 3;
        ctx.setLineDash([16, 12]);
        ctx.lineDashOffset = -time * 34;
        ctx.shadowColor = "rgba(255,190,60,.9)";
        ctx.shadowBlur = 18;
        ctx.beginPath();
        ctx.arc(x, y, outerRadius + 30 + Math.sin(time * 3) * 3, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }

      if (name) {
        const label = getLabelSprite(name, isPlayer);
        const labelY = y - outerRadius - 12;
        ctx.drawImage(label.canvas, x - label.width / 2, labelY - label.height / 2, label.width, label.height);
        if (hunted) {
          ctx.save();
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          // Sits above the name and bobs, because at 17px it was competing with the
          // orbit for attention and losing. Outlined as well as glowing: a gold glyph
          // over a bright orbit is legible only if it has its own dark edge.
          const crownY = labelY - label.height * 0.5 - 17 + Math.sin(time * 3) * 2.5;
          ctx.font = "900 30px Arial";
          ctx.lineWidth = 5;
          ctx.strokeStyle = "rgba(4,10,26,.9)";
          ctx.strokeText("♛", x, crownY);
          ctx.fillStyle = "#ffcf5c";
          ctx.shadowColor = "rgba(255,180,40,.95)";
          ctx.shadowBlur = 22;
          ctx.fillText("♛", x, crownY);
          // A second pass through the glow, so it reads as lit rather than tinted.
          ctx.fillText("♛", x, crownY);
          ctx.restore();
        }
      }
    };

    /** Translates the browser's raw key/pointer state into one simulation input. */
    const readInput = (): PlayerInput => {
      const keys = keysRef.current;
      let moveX = 0;
      let moveY = 0;
      if (keys.KeyW || keys.ArrowUp) moveY -= 1;
      if (keys.KeyS || keys.ArrowDown) moveY += 1;
      if (keys.KeyA || keys.ArrowLeft) moveX -= 1;
      if (keys.KeyD || keys.ArrowRight) moveX += 1;
      const aim = pointerRef.current;
      const input: PlayerInput = {
        moveX,
        moveY,
        aimX: aim.x,
        aimY: aim.y,
        // Aim is where the mouse is; it no longer implies a direction to travel in.
        aiming: aim.active,
        attack: Boolean(keys.Space) || attackHeldRef.current || attackRequestedRef.current,
        boost: Boolean(keys.ShiftLeft || keys.ShiftRight) || boostHeldRef.current || boostRequestedRef.current,
        shield: shieldRequestedRef.current,
      };
      attackRequestedRef.current = false;
      boostRequestedRef.current = false;
      shieldRequestedRef.current = false;
      return input;
    };

    /**
     * The only place simulation facts become toasts, sounds and React state. Events
     * that are not about the local participant are ignored here — with 20 players in
     * an arena, announcing everyone's steals would be noise.
     */
    const consumeEvents = (game: GameData, events: GameEvent[]) => {
      const mine = (id: string) => id === localIdRef.current;
      const fx = feedbackRef.current;
      /** Shake is the one part of this that someone can have asked not to receive. */
      const shake = (amount: number) => {
        if (!reducedMotionRef.current) addTrauma(fx, amount);
      };
      for (const event of events) {
        switch (event.type) {
          case "shot-fired":
            if (!mine(event.participant)) break;
            getSound().play("shoot");
            advanceTutorial(1);
            showToast(tRef.current("toastLaunched"));
            break;
          case "boost-spent":
            if (!mine(event.participant)) break;
            getSound().play("boost");
            showToast(tRef.current("toastBoost"));
            break;
          case "shield-raised":
            if (!mine(event.participant)) break;
            getSound().play("shield");
            showToast(tRef.current("toastShield"));
            break;
          case "pickup-own":
            if (!mine(event.participant)) break;
            getSound().play("pickup");
            advanceTutorial(0);
            // No toast for a pickup — it happens several times a second and the
            // toast bar would never say anything else. The figure at the particle
            // is enough, and it is where the player is already looking.
            if (event.score > 0) {
              pushNumber(fx, { x: event.x, y: event.y, text: `+${event.score}`, color: "#8ef0ff" });
            }
            break;
          case "pickup-enemy":
            if (!mine(event.participant)) break;
            if (event.score > 0) {
              pushNumber(fx, { x: event.x, y: event.y, text: `+${event.score}`, color: "#ffd08a" });
            }
            break;
          case "shield-capture":
            if (!mine(event.participant)) break;
            getSound().play("shield");
            advanceTutorial(3);
            shake(TRAUMA.block);
            pushRing(fx, { x: event.x, y: event.y, color: "#9df6ff", maxRadius: 92, width: 4 });
            pushNumber(fx, { x: event.x, y: event.y, text: `+${event.score}`, color: "#9df6ff" });
            showToast(tRef.current("toastShieldCatch"));
            break;
          case "steal":
            if (mine(event.attacker)) {
              getSound().play("steal");
              advanceTutorial(2);
              shake(TRAUMA.steal);
              pushRing(fx, { x: event.x, y: event.y, color: event.victimColor, maxRadius: 78 });
              pushNumber(fx, { x: event.x, y: event.y, text: `+${event.score}`, color: "#b6ffd0", weight: "loud" });
              showToast(tRef.current("toastStole", { name: event.victimName }));
            } else if (mine(event.victim)) {
              getSound().play("hit");
              shake(TRAUMA.robbed);
              addFlash(fx, 0.4, "255,120,120");
              pushRing(fx, { x: event.x, y: event.y, color: "#ff8080", maxRadius: 78, width: 4 });
              pushNumber(fx, { x: event.x, y: event.y, text: `−${event.taken}`, color: "#ff9d9d", weight: "loud" });
              showToast(tRef.current("toastRobbed", { name: event.attackerName }));
            }
            break;
          case "eliminate":
            // Unlike every other event, this one is heard even when it is somebody
            // else's: the feed is how a player learns the arena is dangerous.
            pushFeed(fx, {
              attacker: event.attackerName,
              attackerColor: event.attackerColor,
              victim: event.victimName,
              victimColor: event.victimColor,
              involved: mine(event.attacker) || mine(event.victim),
            });
            if (mine(event.victim)) {
              getSound().play("eliminate");
              shake(TRAUMA.death);
              addFlash(fx, 0.85, "255,90,90");
              pushRing(fx, { x: event.x, y: event.y, color: "#ff6b6b", maxRadius: 190, width: 6, seconds: 0.8 });
              resetStreak(fx);
              setGameOver(true);
              showToast(tRef.current("toastEliminated"));
            } else if (mine(event.attacker)) {
              getSound().play("eliminate");
              shake(TRAUMA.kill);
              pushRing(fx, { x: event.x, y: event.y, color: event.victimColor, maxRadius: 165, width: 5, seconds: 0.7 });
              pushNumber(fx, { x: event.x, y: event.y, text: `+${event.score}`, color: "#ffe9a3", weight: "loud" });
              const milestone = registerKill(fx);
              if (milestone) getSound().play("streak");
              showToast(tRef.current("toastKill", { name: event.victimName, score: ELIMINATION_SCORE }));
            }
            break;
          case "surge-armed":
            if (mine(event.participant)) {
              getSound().play("shield");
              shake(TRAUMA.charge);
              showToast(tRef.current("toastCharged", { ring: event.tier, orbs: surgeSteal(event.tier) }));
            }
            break;
          case "bounty":
            // Measured: the mark changes hands about nine times a minute, and
            // forty-five of every fifty-three of those are the hunted being hunted
            // down — the loop working, not flicker. But a toast every seven seconds
            // about somebody else is noise. The crown is ambient information: it is
            // on the core, on the radar and on the scoreboard already. Only the
            // moment it lands on *you* is worth interrupting for.
            if (event.participant === null || !mine(event.participant)) break;
            getSound().play("streak");
            shake(TRAUMA.charge);
            showToast(tRef.current("toastHuntedYou"));
            break;
          case "storm": {
            // The warning is the loud one, because it is the only one you can still
            // act on. By the time it opens, either you set off or you did not.
            const warning = event.phase === "warning";
            // Each storm starts the allowance over, so the tally does too.
            stormTakenRef.current = 0;
            getSound().play(warning ? "shield" : "streak");
            // A ring at the place itself, so the announcement has somewhere to point.
            pushRing(fx, { x: event.x, y: event.y, color: STORM_COLOR, maxRadius: 260, width: 5, seconds: 1 });
            showToast(warning
              ? tRef.current("toastStormWarning", { seconds: event.seconds })
              : tRef.current("toastStormOpen", { count: settingsRef.current.stationBonus }));
            break;
          }
          case "storm-orb": {
            if (!mine(event.participant)) break;
            getSound().play("pickup");
            const taker = findParticipant(game, event.participant);
            // One orb at a time, counted up on the spot, so it is obvious that
            // standing here is paying and leaving stops it.
            stormTakenRef.current += event.added;
            if (taker) pushNumber(fx, { x: taker.x, y: taker.y, text: "+1 ⬢", color: STORM_COLOR });
            break;
          }
          case "time-up":
            setGameOver(true);
            showToast(tRef.current("toastTimeUp"));
            break;
        }
      }
    };

    // Server-sent events arrive on the socket, outside this loop.
    consumeEventsRef.current = (events: GameEvent[]) => {
      const game = gameRef.current;
      if (game) consumeEvents(game, events);
    };

    const draw = (game: GameData, now: number) => {
      const { width, height } = game;
      const time = now / 1000;
      // Orbit rotation is simulation state, not decoration: the server picks the
      // launch point from the same clock, so online it must be the server's.
      const orbitTime = (arenaRef.current?.serverNow() ?? now) / 1000;
      if (backgroundTheme !== settingsRef.current.theme || backgroundCanvas.width !== canvas.width || backgroundCanvas.height !== canvas.height) {
        renderStaticBackground(viewportWidth, viewportHeight);
      }
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(backgroundCanvas, 0, 0);
      ctx.restore();

      const camera = cameraRef.current;
      // The shake displaces the world, not the background: the nebula is painted flat
      // behind everything, so leaving it still reads as the camera moving rather than
      // the whole image sliding. The HUD is DOM and never moves — a shaking scoreboard
      // is nausea, a shaking arena is impact.
      const fx = feedbackRef.current;
      const jolt = shakeOffset(fx, time);
      ctx.save();
      ctx.setTransform(
        renderScale,
        0,
        0,
        renderScale,
        (-camera.x + jolt.x) * renderScale,
        (-camera.y + jolt.y) * renderScale,
      );
      drawWorldGrid(camera);

      ctx.save();
      ctx.globalAlpha = 0.35;
      ctx.strokeStyle = "rgba(81,213,255,.72)";
      ctx.lineWidth = 2;
      // The same numbers the simulation fences with, so the line means something.
      ctx.strokeRect(ARENA_EDGE, ARENA_TOP, width - ARENA_EDGE * 2, height - ARENA_TOP - ARENA_EDGE);
      ctx.restore();

      for (let i = 0; i < 120; i += 1) {
        const x = (i * 97.3 + 17) % Math.max(1, width);
        const y = (i * 181.7 + 39) % Math.max(1, height);
        if (!isWorldPointVisible(x, y, camera, { width: viewportWidth, height: viewportHeight }, 2)) continue;
        const alpha = 0.22 + Math.sin(time * 1.4 + i) * 0.12;
        ctx.fillStyle = `rgba(178,209,255,${alpha})`;
        ctx.fillRect(x, y, i % 7 === 0 ? 1.7 : 1, i % 7 === 0 ? 1.7 : 1);
      }

      const stormState = getStormState(
        Number.isFinite(game.elapsed) ? game.elapsed : 0,
        settingsRef.current.stationInterval,
        settingsRef.current.stationActiveDuration,
      );
      const stormRadius = settingsRef.current.stationRadius;
      const storm = getStormCenter(stormState.cycle, width, height, stormRadius);
      // Drawn during the warning as well, and dimmer. Knowing where it will be is
      // the whole reason anybody sets off before it opens; a storm that appears
      // without notice belongs to whoever happened to be standing there.
      if ((stormState.active || stormState.warning)
        && isWorldPointVisible(storm.x, storm.y, camera, { width: viewportWidth, height: viewportHeight }, stormRadius + 140)) {
        const open = stormState.active;
        ctx.save();
        ctx.textAlign = "center";
        ctx.globalAlpha = open ? 0.95 : 0.5;
        ctx.strokeStyle = STORM_COLOR;
        // The ring pulses, so it cannot be a sprite — but it does not need a blur
        // pass either; the baked core below carries the glow.
        ctx.lineWidth = open ? 4 : 2;
        ctx.beginPath();
        ctx.arc(storm.x, storm.y, stormRadius * (open ? 1 + Math.sin(time * 7) * 0.08 : 1), 0, Math.PI * 2);
        ctx.stroke();

        if (open) {
          for (let wave = 0; wave < 3; wave += 1) {
            const phase = (time * 0.62 + wave / 3) % 1;
            ctx.globalAlpha = (1 - phase) * 0.48;
            ctx.beginPath();
            ctx.arc(storm.x, storm.y, stormRadius * (1 + phase * 1.35), 0, Math.PI * 2);
            ctx.stroke();
          }
          ctx.globalAlpha = 0.95;
        } else {
          // A second ring closing in on the first as the countdown runs. The gap is
          // the clock, and it is readable from further off than a number would be.
          const closing = 1 + (stormState.nextActivation / STORM_WARNING_SECONDS) * 1.6;
          ctx.globalAlpha = 0.42;
          ctx.setLineDash([13, 11]);
          ctx.lineDashOffset = -time * 30;
          ctx.beginPath();
          ctx.arc(storm.x, storm.y, stormRadius * closing, 0, Math.PI * 2);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.globalAlpha = 0.5;
        }

        ctx.beginPath();
        ctx.arc(storm.x, storm.y, 32 + Math.sin(time * 2) * 2, 0, Math.PI * 2);
        ctx.stroke();
        const core = getStationSprite(open);
        ctx.drawImage(core, storm.x - 42, storm.y - 42, 84, 84);

        // What the storm owes *you*, drawn on the storm rather than in a corner —
        // there is nowhere else the player is looking while standing in it. Without
        // this the rule is invisible: orbs simply arrive, and it is impossible to
        // tell that the allowance is per player rather than a pool being raced for.
        const takenHere = Math.min(stormTakenRef.current, settingsRef.current.stationBonus);
        if (open && takenHere > 0) {
          ctx.save();
          ctx.lineCap = "round";
          ctx.lineWidth = 6;
          ctx.strokeStyle = "rgba(6,14,32,.75)";
          ctx.beginPath();
          ctx.arc(storm.x, storm.y, 44, -Math.PI / 2, Math.PI * 1.5);
          ctx.stroke();
          ctx.strokeStyle = "#ffe9a3";
          ctx.beginPath();
          ctx.arc(
            storm.x,
            storm.y,
            44,
            -Math.PI / 2,
            -Math.PI / 2 + (takenHere / settingsRef.current.stationBonus) * Math.PI * 2,
          );
          ctx.stroke();
          ctx.restore();
        }

        ctx.fillStyle = STORM_COLOR;
        ctx.font = "900 12px Arial";
        ctx.fillText(
          open
            ? tRef.current("stormOpenMark", { seconds: Math.ceil(stormState.activeRemaining) })
            : tRef.current("stormWarnMark", { seconds: Math.ceil(stormState.nextActivation) }),
          storm.x,
          storm.y + stormRadius + 19,
        );
        if (open) {
          ctx.fillStyle = "#ffe9a3";
          ctx.font = "900 13px Arial";
          ctx.fillText(`${takenHere} / ${settingsRef.current.stationBonus} ⬢`, storm.x, storm.y + stormRadius + 36);
        }
        ctx.restore();
      }

      game.energy.forEach((item) => {
        if (!isWorldPointVisible(item.x, item.y, camera, { width: viewportWidth, height: viewportHeight }, 20)) return;
        const pulse = 1 + Math.sin(time * 3 + item.phase) * 0.18;
        drawGlowCircle(item.x, item.y, item.size * pulse, item.color);
        ctx.fillStyle = "rgba(255,255,255,.75)";
        ctx.beginPath();
        ctx.arc(item.x - 1, item.y - 1, Math.max(1, item.size * 0.28), 0, Math.PI * 2);
        ctx.fill();
      });

      const activeBoosts = game.participants.reduce((count, participant) => count + (participant.alive && participant.boostRemaining > 0 ? 1 : 0), 0);
      const enhancedBoostFx = activeBoosts <= 3 && renderScale >= 0.9;
      // Everyone except the local core, so other humans render exactly like bots do.
      game.participants.forEach((participant, index) => {
        if (participant.id === localIdRef.current || !participant.alive) return;
        if (!isWorldPointVisible(participant.x, participant.y, camera, { width: viewportWidth, height: viewportHeight }, 120)) return;
        const phase = orbitPhaseFor(participant.id, orbitTime);
        if (participant.boostRemaining > 0) drawBoostTrail(participant.x, participant.y, participant.vx, participant.vy, participant.color, time + index, enhancedBoostFx);
        drawCore(participant.x, participant.y, participant.color, participant.orbs, phase, false, participant.shield > 0, participant.name, participant.spawnProtection > 0, chargeOf(participant), isBounty(game, participant));
      });

      game.shots.forEach((shot) => {
        if (!shot.active || !isWorldPointVisible(shot.x, shot.y, camera, { width: viewportWidth, height: viewportHeight }, 60)) return;
        const velocityLength = Math.max(1, Math.hypot(shot.vx, shot.vy));
        const trailLength = shot.stolen ? 44 : 26;
        const trailX = shot.x - (shot.vx / velocityLength) * trailLength;
        const trailY = shot.y - (shot.vy / velocityLength) * trailLength;
        ctx.save();
        ctx.strokeStyle = shot.color;
        // No shadowBlur on the trail: it is two translucent lines and the head
        // already carries a baked glow sprite, so the blur pass bought nothing but
        // cost one per shot per frame.
        ctx.globalAlpha = shot.stolen ? 0.22 : 0.16;
        ctx.lineWidth = shot.stolen ? 8 : 5;
        ctx.beginPath();
        ctx.moveTo(trailX, trailY);
        ctx.lineTo(shot.x, shot.y);
        ctx.stroke();
        ctx.globalAlpha = shot.stolen ? 0.78 : 0.5;
        ctx.lineWidth = shot.stolen ? 3 : 2;
        ctx.beginPath();
        ctx.moveTo(trailX, trailY);
        ctx.lineTo(shot.x, shot.y);
        ctx.stroke();
        ctx.restore();
        // A charged orb arrives with current running down it, so an opponent can
        // see what is coming and the player can see what they spent.
        if (ORB_ARCS && shot.surge > 0) {
          ctx.save();
          ctx.lineCap = "round";
          ctx.lineWidth = 4 + shot.surge * 1.6;
          ctx.strokeStyle = colorWithAlpha(shot.color, 0.24 + shot.surge * 0.08);
          drawArc(ctx, trailX, trailY, shot.x, shot.y, shot.id, time);
          ctx.lineWidth = 1.2 + shot.surge * 0.45;
          ctx.strokeStyle = "rgba(240,252,255,.95)";
          drawArc(ctx, trailX, trailY, shot.x, shot.y, shot.id + 7, time);
          ctx.restore();
        }
        drawGlowCircle(shot.x, shot.y, shot.surge > 0 ? 9 + shot.surge : shot.stolen ? 9 : 7, shot.color);
        if (shot.stolen) {
          ctx.strokeStyle = "rgba(255,255,255,.8)";
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(shot.x, shot.y, 13, 0, Math.PI * 2);
          ctx.stroke();
        }
      });

      const localCore = findParticipant(game, localIdRef.current);
      if (localCore?.alive) {
        if (localCore.boostRemaining > 0) drawBoostTrail(localCore.x, localCore.y, localCore.vx, localCore.vy, localCore.color, time, enhancedBoostFx);
        drawCore(localCore.x, localCore.y, localCore.color, localCore.orbs, orbitPhaseFor(localCore.id, orbitTime), true, localCore.shield > 0, `${localCore.name} · ${tRef.current("you")}`, localCore.spawnProtection > 0, chargeOf(localCore), isBounty(game, localCore));
      }

      game.sparks.forEach((spark) => {
        if (!spark.active || !isWorldPointVisible(spark.x, spark.y, camera, { width: viewportWidth, height: viewportHeight }, 20)) return;
        ctx.save();
        ctx.globalAlpha = clamp(spark.life * 1.7, 0, 1);
        drawGlowCircle(spark.x, spark.y, 2.5, spark.color);
        ctx.restore();
      });

      // A shell leaving the impact. Two strokes with the outer one wider and fainter
      // gives it a soft edge without a blur pass, the same trick the shot trails use.
      for (const ring of fx.rings) {
        if (!ring.active) continue;
        if (!isWorldPointVisible(ring.x, ring.y, camera, { width: viewportWidth, height: viewportHeight }, ring.maxRadius)) continue;
        const fade = ring.life / ring.maxLife;
        ctx.save();
        ctx.strokeStyle = ring.color;
        ctx.globalAlpha = fade * 0.28;
        ctx.lineWidth = ring.width * 2.6;
        ctx.beginPath();
        ctx.arc(ring.x, ring.y, ring.radius, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = fade * 0.9;
        ctx.lineWidth = ring.width;
        ctx.beginPath();
        ctx.arc(ring.x, ring.y, ring.radius, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }

      // Drawn last inside the world so nothing covers a figure the player needs.
      for (const number of fx.numbers) {
        if (!number.active) continue;
        if (!isWorldPointVisible(number.x, number.y, camera, { width: viewportWidth, height: viewportHeight }, 40)) continue;
        const progress = number.life / number.maxLife;
        const loud = number.weight === "loud";
        ctx.save();
        // Full opacity for most of the flight, then out: a figure that starts fading
        // the instant it appears is one the player has to guess at.
        ctx.globalAlpha = clamp(progress * 2.4, 0, 1);
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.font = `900 ${loud ? 21 : 15}px Arial`;
        ctx.lineWidth = loud ? 4.5 : 3;
        ctx.strokeStyle = "rgba(3,8,22,.85)";
        ctx.strokeText(number.text, number.x, number.y);
        ctx.fillStyle = number.color;
        ctx.fillText(number.text, number.x, number.y);
        ctx.restore();
      }

      if (pointerRef.current.active && game.running && localCore) {
        const aimDx = pointerRef.current.x - localCore.x;
        const aimDy = pointerRef.current.y - localCore.y;
        const aimDistance = Math.max(1, Math.hypot(aimDx, aimDy));
        const range = settingsRef.current.shotRange;
        const visibleDistance = Math.min(aimDistance, range);
        const aimX = localCore.x + (aimDx / aimDistance) * visibleDistance;
        const aimY = localCore.y + (aimDy / aimDistance) * visibleDistance;
        const beyondRange = aimDistance > range;
        const aimAngle = Math.atan2(aimDy, aimDx);
        ctx.save();
        ctx.strokeStyle = beyondRange ? "rgba(255,180,91,.72)" : "rgba(105,232,255,.5)";
        ctx.lineWidth = beyondRange ? 1.5 : 1;
        ctx.setLineDash([4, 7]);
        ctx.beginPath();
        ctx.moveTo(localCore.x, localCore.y);
        ctx.lineTo(aimX, aimY);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.arc(aimX, aimY, 8 + Math.sin(time * 4) * 2, 0, Math.PI * 2);
        ctx.stroke();
        if (beyondRange) {
          ctx.globalAlpha = 0.55;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(localCore.x, localCore.y, range, aimAngle - 0.12, aimAngle + 0.12);
          ctx.stroke();
          ctx.globalAlpha = 0.85;
          ctx.fillStyle = "#ffd09a";
          ctx.font = "800 8px Arial";
          ctx.textAlign = "center";
          ctx.fillText(tRef.current("aimRange"), aimX, aimY - 15);
        }
        ctx.restore();
      }
      ctx.restore();

      // Being hit, as seen from inside the core: a wash at the edges of the screen
      // rather than over the middle, so it cannot hide the thing that is shooting at
      // you. Screen space, after the world transform is released.
      if (fx.flash > 0.01) {
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        const edge = ctx.createRadialGradient(
          canvas.width / 2, canvas.height / 2, Math.min(canvas.width, canvas.height) * 0.28,
          canvas.width / 2, canvas.height / 2, Math.max(canvas.width, canvas.height) * 0.62,
        );
        edge.addColorStop(0, `rgba(${fx.flashColor},0)`);
        edge.addColorStop(1, `rgba(${fx.flashColor},${fx.flash * 0.5})`);
        ctx.fillStyle = edge;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.restore();
      }
    };

    const syncUi = (game: GameData) => {
      const local = findParticipant(game, localIdRef.current);
      if (!local) return;
      setScore(local.score);
      setOrbs(local.orbs);
      setSurge({
        tier: surgeTier(local.orbs),
        wait: Math.ceil(local.surgeCooldown),
      });
      setTimeLeft(Math.ceil(game.timeLeft));
      setEndless(game.matchDuration <= 0);
      // These two tick down every frame. Feeding React the raw float re-rendered the
      // whole page 10x a second; the HUD only ever shows the rounded value anyway.
      setShieldCooldown(Math.ceil(local.shieldCooldown));
      setBoostRemaining(Math.round(local.boostRemaining * 10) / 10);

      const all: Leader[] = game.participants.map((participant) => ({
        id: participant.id,
        name: participant.name,
        score: participant.score,
        color: participant.color,
      }));
      setHunted(game.bounty ?? null);
      const sorted = all.sort((a, b) => b.score - a.score);
      setRank(sorted.findIndex((leader) => leader.id === local.id) + 1);
      setPlayerCount(all.length);

      // The two parts of the feedback state that are DOM. Both are published by
      // identity rather than by copying every tick: a fresh array always re-renders,
      // and the feed changes a few times a minute, not ten times a second.
      const fx = feedbackRef.current;
      const feedSignature = fx.feed.map((entry) => entry.id).join(",");
      if (feedSignature !== feedSignatureRef.current) {
        feedSignatureRef.current = feedSignature;
        setFeed([...fx.feed]);
      }
      const bannerId = fx.banner?.id ?? 0;
      if (bannerId !== bannerIdRef.current) {
        bannerIdRef.current = bannerId;
        setStreak(fx.banner ? { id: fx.banner.id, count: fx.banner.count } : null);
      }

      const times = frameTimesRef.current;
      if (times.length > 10) {
        const average = times.reduce((sum, value) => sum + value, 0) / times.length;
        setFps(Math.round(1 / average));
        // The average hides stutter; the worst frame in the window is what you feel.
        setWorstFrame(Math.round(Math.max(...times) * 1000));
      }
      const loops = loopTimesRef.current;
      if (loops.length > 10) setWorstLoop(Math.round(Math.max(...loops)));
      setPing(Math.round(arenaRef.current?.pingMs ?? 0));
      setInputLag(Math.round(arenaRef.current?.latencyMs ?? 0));
      // A fresh array always re-renders, so only publish one when the board changed.
      const top = sorted.slice(0, 5);
      const signature = top.map((leader) => `${leader.name}:${leader.score}`).join("|");
      if (signature !== leaderSignatureRef.current) {
        leaderSignatureRef.current = signature;
        setLeaders(top);
      }

      const minimap = minimapRef.current;
      if (!minimap) return;
      const rect = minimap.getBoundingClientRect();
      const scale = Math.min(2, window.devicePixelRatio || 1);
      const pixelWidth = Math.max(1, Math.round(rect.width * scale));
      const pixelHeight = Math.max(1, Math.round(rect.height * scale));
      if (minimap.width !== pixelWidth || minimap.height !== pixelHeight) {
        minimap.width = pixelWidth;
        minimap.height = pixelHeight;
      }
      const mapCtx = minimap.getContext("2d");
      if (!mapCtx) return;
      mapCtx.setTransform(scale, 0, 0, scale, 0, 0);
      mapCtx.clearRect(0, 0, rect.width, rect.height);
      mapCtx.fillStyle = "rgba(2,8,25,.78)";
      mapCtx.fillRect(0, 0, rect.width, rect.height);
      mapCtx.strokeStyle = "rgba(107,203,255,.14)";
      mapCtx.lineWidth = 1;
      for (let index = 1; index < 4; index += 1) {
        mapCtx.beginPath();
        mapCtx.moveTo((rect.width * index) / 4, 0);
        mapCtx.lineTo((rect.width * index) / 4, rect.height);
        mapCtx.moveTo(0, (rect.height * index) / 4);
        mapCtx.lineTo(rect.width, (rect.height * index) / 4);
        mapCtx.stroke();
      }
      const cameraWindow = getMinimapViewport(cameraRef.current.x, cameraRef.current.y, viewportWidth, viewportHeight, game.width, game.height, rect.width, rect.height);
      mapCtx.strokeStyle = "rgba(185,242,255,.5)";
      mapCtx.strokeRect(cameraWindow.x, cameraWindow.y, cameraWindow.width, cameraWindow.height);
      // The radar is where a player decides where to go, which makes it the most
      // important place the warning appears — more than the arena ring, which is
      // only visible once you are already close enough not to need telling.
      const mapStorm = getStormState(
        Number.isFinite(game.elapsed) ? game.elapsed : 0,
        settingsRef.current.stationInterval,
        settingsRef.current.stationActiveDuration,
      );
      if (mapStorm.active || mapStorm.warning) {
        const centre = getStormCenter(mapStorm.cycle, game.width, game.height, settingsRef.current.stationRadius);
        const marker = projectToMinimap(centre.x, centre.y, game.width, game.height, rect.width, rect.height);
        const flash = mapStorm.active ? 0.45 + (Math.sin(performance.now() * 0.012) + 1) * 0.275 : 0.72;
        const markerSize = mapStorm.active ? 9 : 6;
        mapCtx.globalAlpha = flash;
        mapCtx.fillStyle = mapStorm.active ? "#fff7a8" : STORM_COLOR;
        mapCtx.fillRect(marker.x - markerSize * 0.5, marker.y - markerSize * 0.5, markerSize, markerSize);
        mapCtx.globalAlpha = Math.max(0.2, flash - 0.2);
        mapCtx.strokeStyle = STORM_COLOR;
        mapCtx.lineWidth = 1.5;
        mapCtx.beginPath();
        mapCtx.arc(marker.x, marker.y, mapStorm.active ? 5 + flash * 4 : 11, 0, Math.PI * 2);
        mapCtx.stroke();
        mapCtx.globalAlpha = 1;
      }
      for (const participant of game.participants) {
        if (!participant.alive || participant.id === localIdRef.current) continue;
        const marker = projectToMinimap(participant.x, participant.y, game.width, game.height, rect.width, rect.height);
        mapCtx.fillStyle = participant.color;
        mapCtx.beginPath();
        mapCtx.arc(marker.x, marker.y, 3.2, 0, Math.PI * 2);
        mapCtx.fill();
        // The radar is where you decide where to go, so the mark has to be on it.
        if (isBounty(game, participant)) {
          mapCtx.strokeStyle = "#ffc94a";
          mapCtx.lineWidth = 1.6;
          mapCtx.beginPath();
          mapCtx.arc(marker.x, marker.y, 6 + (Math.sin(performance.now() * 0.007) + 1) * 1.6, 0, Math.PI * 2);
          mapCtx.stroke();
        }
      }
      const playerMarker = projectToMinimap(local.x, local.y, game.width, game.height, rect.width, rect.height);
      mapCtx.fillStyle = local.color;
      mapCtx.strokeStyle = "#ffffff";
      mapCtx.lineWidth = 1.5;
      mapCtx.beginPath();
      mapCtx.arc(playerMarker.x, playerMarker.y, 4, 0, Math.PI * 2);
      mapCtx.fill();
      mapCtx.stroke();
    };

    let uiTimer = 0;
    let accumulator = 0;
    const loop = (now: number) => {
      const game = gameRef.current;
      if (!game) return;

      const elapsed = Math.max(0, (now - game.lastTime) / 1000);
      game.lastTime = now;
      // Frame times, so the HUD can report the real draw rate. Measuring this from
      // outside the browser is impossible, and it is the number that says whether a
      // stutter is the network or the renderer.
      if (elapsed > 0) {
        const times = frameTimesRef.current;
        times.push(elapsed);
        if (times.length > 120) times.shift();
      }
      // Fixed timestep: the simulation always advances in identical slices, so it
      // behaves the same on a 60Hz laptop, a 144Hz monitor and a headless server.
      // MAX_FRAME_SECONDS stops a backgrounded tab from spiralling on return.
      accumulator = Math.min(accumulator + elapsed, MAX_FRAME_SECONDS);

      let simulated = 0;
      const arena = arenaRef.current;
      if (onlineRef.current && arena) {
        // Online the arena owns the world: we only publish intent and draw what it
        // sends back. No local stepping, so nothing here can disagree with the
        // server about where anyone is.
        syncAimFromScreen();
        arena.setInput(readInput());
        // No timestamp: the client keeps its own clock, on the same origin as rAF.
        // `tick` advances the predicted local core every frame and sends intent when
        // the input interval is up.
        arena.usePredictionWorld(game);
        arena.tick(Math.min(elapsed, MAX_FRAME_SECONDS));
        const world = arena.sample();
        if (world && arena.you) {
          localIdRef.current = arena.you;
          applySnapshot(game, world, arena.you);
        }
        // Sparks are local decoration; they still need to age.
        simulated = Math.min(elapsed, MAX_FRAME_SECONDS);
        accumulator = 0;
        advanceLocalSparks(game, simulated);
      } else {
        while (accumulator >= FIXED_TIMESTEP) {
          syncAimFromScreen();
          // Read through the ref, so toggling pause no longer tears down and rebuilds
          // the whole render loop.
          if (!pausedRef.current) {
            const inputs = { [LOCAL_PLAYER_ID]: readInput() };
            consumeEvents(game, stepGame(game, inputs, FIXED_TIMESTEP, settingsRef.current));
          }
          accumulator -= FIXED_TIMESTEP;
          simulated += FIXED_TIMESTEP;
        }
      }

      const focus = findParticipant(game, localIdRef.current);
      if (focus) followCamera(cameraRef.current, focus.x, focus.y, game, { width: viewportWidth, height: viewportHeight }, Math.max(simulated, elapsed));

      // In a corner the camera has nowhere left to give, and the core ends up under
      // the radar or the scoreboard. Rather than pulling the view further into empty
      // space, the panel that is in the way steps back.
      if (focus) {
        const reach = getCollisionRadius(focus.orbs, settingsRef.current) + 10;
        const screenX = focus.x - cameraRef.current.x;
        const screenY = focus.y - cameraRef.current.y;
        for (const panel of [radarPanelRef.current, boardPanelRef.current]) {
          if (!panel) continue;
          const box = panel.getBoundingClientRect();
          const covered = screenX + reach > box.left && screenX - reach < box.right
            && screenY + reach > box.top && screenY - reach < box.bottom;
          panel.classList.toggle("is-behind", covered);
        }
      }
      syncAimFromScreen();
      // Ages on wall-clock time, not simulation time: the shake from the orb that
      // just killed you has to finish decaying while the match is paused behind the
      // result card, or it is frozen on screen under the modal.
      advanceFeedback(feedbackRef.current, elapsed);
      draw(game, now);
      uiTimer += elapsed;
      if (uiTimer > 0.1) {
        syncUi(game);
        uiTimer = 0;
      }
    };

    let animationFrame = 0;
    const animationLoop = (now: number) => {
      // Time the loop body separately from the frame gap. If frames are long but the
      // body is short, the stall is outside this code — React, GC or the compositor.
      const startedAt = performance.now();
      loop(now);
      const times = loopTimesRef.current;
      times.push(performance.now() - startedAt);
      if (times.length > 120) times.shift();
      animationFrame = window.requestAnimationFrame(animationLoop);
    };

    resize();
    animationFrame = window.requestAnimationFrame(animationLoop);
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
    };
  }, [advanceTutorial, showToast, syncAimFromScreen]);

  useEffect(() => {
    // Sliders, selects and the color picker live inside the settings dialog. The
    // game must not swallow their keys, or arrows and Space stop adjusting them.
    // Buttons are deliberately NOT excluded: a focused HUD button must not steal
    // Space away from the attack control.
    const isFormControl = (target: EventTarget | null) =>
      target instanceof HTMLElement
      && (["INPUT", "SELECT", "TEXTAREA"].includes(target.tagName) || target.isContentEditable);

    const onKeyDown = (event: KeyboardEvent) => {
      if (isFormControl(event.target)) return;
      keysRef.current[event.code] = true;
      if (["Space", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.code)) event.preventDefault();
      if (event.code === "Space" && !event.repeat) requestAttack();
      if (["ShiftLeft", "ShiftRight"].includes(event.code) && !event.repeat) requestBoost();
      if (event.code === "KeyE" && !event.repeat) requestShield();
      if (event.code === "KeyP" && !event.repeat && started && !gameOver) setPaused((value) => !value);
    };
    const onKeyUp = (event: KeyboardEvent) => {
      keysRef.current[event.code] = false;
    };
    // Alt-tabbing away swallows the keyup, so a held key would stay down forever
    // and the core would keep drifting after the player comes back.
    window.addEventListener("keydown", onKeyDown, { passive: false });
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", releaseAllInputs);
    // Only the finger that pressed a button may release it, otherwise lifting the
    // steering thumb also cancels a held attack or boost on multi-touch.
    const releaseHeldButtons = (event: PointerEvent) => {
      if (event.pointerId === attackPointerIdRef.current) {
        attackHeldRef.current = false;
        attackPointerIdRef.current = null;
      }
      if (event.pointerId === boostPointerIdRef.current) {
        boostHeldRef.current = false;
        boostPointerIdRef.current = null;
      }
    };
    window.addEventListener("pointerup", releaseHeldButtons);
    window.addEventListener("pointercancel", releaseHeldButtons);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", releaseAllInputs);
      window.removeEventListener("pointerup", releaseHeldButtons);
      window.removeEventListener("pointercancel", releaseHeldButtons);
    };
  }, [gameOver, releaseAllInputs, requestAttack, requestBoost, requestShield, started]);

  useEffect(() => { pausedRef.current = paused; }, [paused]);

  useEffect(() => { getSound().setMuted(muted); }, [muted]);

  useEffect(() => () => {
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    if (persistTimerRef.current) window.clearTimeout(persistTimerRef.current);
    soundRef.current?.dispose();
    // Leaving the page must free the arena seat rather than wait for the heartbeat.
    arenaRef.current?.disconnect();
    arenaRef.current = null;
  }, []);

  // Escape closes whichever overlay is open, and otherwise toggles the pause the
  // same way P does — the modals previously had no keyboard exit at all.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (helpOpen || settingsOpen) {
        event.preventDefault();
        closeOverlay();
        return;
      }
      if (started && !gameOver) {
        event.preventDefault();
        setPaused((value) => !value);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeOverlay, gameOver, helpOpen, settingsOpen, started]);

  // Keep keyboard focus inside the visible dialog. Only one ever renders at a time,
  // so a single ref is enough.
  const modalSignature = `${started}|${gameOver}|${paused}|${helpOpen}|${settingsOpen}`;
  useEffect(() => {
    const node = modalRef.current;
    if (!node) return;
    const focusable = () => Array.from(
      node.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
    ).filter((element) => !element.hasAttribute("disabled") && element.offsetParent !== null);

    focusable()[0]?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const items = focusable();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (event.shiftKey && (active === first || !node.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    node.addEventListener("keydown", onKeyDown);
    return () => node.removeEventListener("keydown", onKeyDown);
  }, [modalSignature]);

  useEffect(() => {
    if (started && gameRef.current && !gameOver) {
      gameRef.current.running = true;
      gameRef.current.lastTime = performance.now();
    }
  }, [started, gameOver]);

  const handlePointer = (event: React.PointerEvent<HTMLCanvasElement>, steer: boolean) => {
    const rect = event.currentTarget.getBoundingClientRect();
    screenPointerRef.current = {
      x: clamp(event.clientX - rect.left, 0, rect.width),
      y: clamp(event.clientY - rect.top, 0, rect.height),
      active: true,
      steer,
    };
    syncAimFromScreen();
  };

  // The mouse aims and fires; the keys move. Steering with the pointer meant the
  // two jobs fought each other — you could not hold a line and keep a target lined
  // up at the same time, because both were the same hand on the same device.
  const isMouseLike = (event: React.PointerEvent<HTMLCanvasElement>) => event.pointerType === "mouse";

  const handleCanvasPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    handlePointer(event, true);
    // Left button only: the right one is for whatever a browser wants it for.
    if (!isMouseLike(event) || event.button === 0) {
      attackPointerIdRef.current = event.pointerId;
      attackHeldRef.current = true;
      requestAttack();
    }
  };

  const handleCanvasPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    handlePointer(event, true);
  };

  const handleCanvasPointerRelease = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (event.pointerId !== attackPointerIdRef.current) return;
    attackPointerIdRef.current = null;
    attackHeldRef.current = false;
  };

  const formatTime = (seconds: number) => `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
  /** Occupancy is only worth fetching when the browser is actually being opened. */
  const openRooms = useCallback(() => {
    setRoomsOpen(true);
    fetch("/api/rooms")
      .then((response) => response.json())
      .then((body) => setRooms(body))
      .catch(() => setRooms({ capacity: 20, rooms: [] }));
  }, []);

  const languageSwitch = (
    <div className="lang-switch" role="group" aria-label={t("languageAria")}>
      {LANGUAGES.map((code) => (
        <button
          key={code}
          type="button"
          aria-pressed={code === language}
          className={code === language ? "is-active" : ""}
          onClick={() => setLanguage(code)}
        >{code.toUpperCase()}</button>
      ))}
    </div>
  );

  return (
    <main className="game-page">
      <section
        className="game-stage"
        data-theme={settings.theme}
        style={{ "--player-color": settings.playerColor } as React.CSSProperties}
        aria-label="N Nova Orbit Clash oynanabilir prototip"
      >
        <canvas
          ref={canvasRef}
          className="game-canvas"
          onPointerMove={handleCanvasPointerMove}
          onPointerDown={handleCanvasPointerDown}
          onPointerUp={handleCanvasPointerRelease}
          onPointerCancel={handleCanvasPointerRelease}
          onContextMenu={(event) => event.preventDefault()}
        />

        <aside className="minimap glass-panel" ref={radarPanelRef} aria-label={t("radarAria")}>
          <div><span>{roomName ? roomLabel(roomName, t) : t("radar")}</span><b>{leaders.length > 0 ? t("players", { count: playerCount }) : "—"}</b></div>
          {SHOW_DIAGNOSTICS && (
            <div className="net-readout"><span>{fps} FPS · {ping}ms PING</span><b>{worstFrame}/{worstLoop} · {inputLag}ms</b></div>
          )}
          <canvas ref={minimapRef} />
        </aside>

        <header className="top-hud">
          <div className="brand-chip glass-panel">
            <span className="brand-mark">N</span>
            <span><b>N NOVA</b><small>ORBIT CLASH</small></span>
          </div>
          <div className="stat-chip glass-panel">
            <span className="stat-label">{t("score")}</span>
            <strong>{score.toLocaleString(locale)}</strong>
          </div>
          <div className="timer-chip glass-panel" aria-label={endless ? t("endlessAria") : t("timeLeftAria", { time: formatTime(timeLeft) })}>
            <span className="timer-icon">◷</span>
            <strong>{endless ? "∞" : formatTime(timeLeft)}</strong>
          </div>
          {/* One group pushed to the right edge. The old arrangement hung
              margin-left:auto off whichever icon button happened to come first,
              which quietly broke the moment anything was added before it. */}
          <div className="hud-actions">
            {languageSwitch}
            {online && (
              <button className="leave-button glass-panel" onClick={leaveOnline} aria-label={t("leaveAria")}>{t("leave")}</button>
            )}
            <button className="icon-button mute-button glass-panel" onClick={() => setMuted((value) => !value)} aria-label={muted ? t("soundOn") : t("soundOff")}>
              {muted ? "◌" : "♪"}
            </button>
            <button className="icon-button glass-panel" onClick={() => openOverlay("settings")} aria-label={t("settingsAria")}>⚙</button>
            <button className="icon-button glass-panel" onClick={() => openOverlay("help")} aria-label={t("helpAria")}>?</button>
          </div>
        </header>

        <aside className="leaderboard glass-panel" ref={boardPanelRef} aria-label={t("rankingAria")}>
          <div className="leader-title"><span>{t("liveRanking")}</span><b>#{rank}</b></div>
          {leaders.map((leader, index) => (
            <div
              className={`leader-row ${leader.id === localId ? "is-you" : ""} ${leader.id === hunted ? "is-hunted" : ""}`}
              key={leader.id}
            >
              {/* The crown takes the rank number's place rather than trailing the
                  name. In that column it is always in the same spot, so the eye
                  finds it without reading the row — and the rank of the hunted is
                  never in question anyway. */}
              {leader.id === hunted
                ? <span className="hunted-crown" title={t("huntedAria")}>♛</span>
                : <span className="rank-number" style={{ color: leader.color }}>{index + 1}</span>}
              <i style={{ background: leader.color }} />
              <b>{leader.name}</b>
              <span>{leader.score.toLocaleString(locale)}</span>
            </div>
          ))}
        </aside>

        {/* Under the scoreboard on the right, where a player already looks to see
            how they are doing. Silent when nothing has happened — an empty box
            reserving space would be worse than the arena it is covering. */}
        {feed.length > 0 && (
          <div className="kill-feed" aria-live="polite">
            {feed.map((entry) => (
              <div className={`kill-row ${entry.involved ? "is-mine" : ""}`} key={entry.id}>
                <b style={{ color: entry.attackerColor }}>{entry.attacker}</b>
                <span className="kill-arrow" aria-label={t("killedAria")}>⟶</span>
                <b style={{ color: entry.victimColor }}>{entry.victim}</b>
              </div>
            ))}
          </div>
        )}

        {streak && (
          <div className="streak-banner" key={streak.id} role="status">
            <strong>{t(streakKey(streak.count))}</strong>
            <small>{t("streakCount", { count: streak.count })}</small>
          </div>
        )}

        {hunted !== null && hunted === localId && (
          <div className="hunted-warning" role="status">
            <span className="hunted-crown">♛</span>{t("huntedYou")}
          </div>
        )}

        <div className={`toast ${shownToast ? "is-visible" : ""}`} aria-live="polite">{shownToast}</div>

        {online && netStatus !== "joined" && (
          <div className="net-banner glass-panel" role="status" aria-live="polite">
            <span className="net-dot" />
            {netStatus === "connecting" && t("connecting")}
            {netStatus === "reconnecting" && t("reconnecting")}
            {netStatus === "closed" && t("disconnected")}
            {netStatus === "idle" && t("preparing")}
          </div>
        )}

        {started && !gameOver && !paused && tutorialStep !== null && (
          <aside className="tutorial-card glass-panel" aria-live="polite">
            <div className="tutorial-progress" aria-label={t("tutorialStepAria", { step: tutorialStep + 1, total: TUTORIAL_STEPS.length })}>
              {TUTORIAL_STEPS.map((step, index) => (
                <i className={index <= tutorialStep ? "is-active" : ""} key={step.title} />
              ))}
            </div>
            <div className="tutorial-content">
              <span className="tutorial-icon">{TUTORIAL_STEPS[tutorialStep].icon}</span>
              <span><b>{t(TUTORIAL_STEPS[tutorialStep].title)}</b><small>{t(TUTORIAL_STEPS[tutorialStep].copy)}</small></span>
            </div>
            <button onClick={() => { tutorialStepRef.current = null; setTutorialStep(null); }} aria-label={t("tutorialSkipAria")}>{t("tutorialSkip")}</button>
          </aside>
        )}

        <div className="orbit-counter glass-panel">
          <span className="mini-orb" />
          <span className="orbit-count"><b>{orbs}</b><small>{t("orbCounter")}</small></span>
          {surge.tier > 0 && (
            <span className={`surge-chip ${surge.wait > 0 ? "is-waiting" : "is-ready"}`}>
              <i aria-hidden="true">⚡</i>
              {surge.wait > 0
                ? <b>{surge.wait}</b>
                : <span><b>{surgeSteal(surge.tier)}</b><small>{t("surgeReady")}</small></span>}
            </span>
          )}
        </div>

        <div className="desktop-help glass-panel">
          <span><kbd>WASD</kbd> {t("keyMove")}</span>
          <span><kbd>{t("mouseLeft")}</kbd> {t("keyAim")}</span>
          <span><kbd>E</kbd> {t("keyShield")}</span>
          <span><kbd>SHIFT</kbd> {t("keyBoost")}</span>
        </div>

        {/* Touch controls. The game is keyboard and mouse only for now — a thumb
            pad on a desktop screen is clutter, and the phone build has not been
            designed yet. The markup stays so that work starts from something that
            already worked, rather than from nothing. */}
        {TOUCH_CONTROLS && (<>
          <div className="mobile-steer" aria-hidden="true">
            <span className="steer-dot" />
            <small>SÜRÜKLE</small>
          </div>

          <div className="action-buttons">
            <button
              className={`boost-button ${boostRemaining > 0 ? "is-active" : ""}`}
              onPointerDown={(event) => { event.stopPropagation(); boostPointerIdRef.current = event.pointerId; boostHeldRef.current = true; requestBoost(); }}
              disabled={orbs < 1 || gameOver}
              aria-label={t("boostAria")}
            >
              <span>»</span>
              <b>BOOST</b>
              {boostRemaining > 0 && <small>{boostRemaining.toFixed(1)}</small>}
            </button>
            <button
              className={`shield-button ${shieldCooldown <= 0 ? "is-ready" : ""}`}
              onPointerDown={(event) => { event.stopPropagation(); requestShield(); }}
              aria-label={t("shieldAria")}
            >
              <span>⬡</span>
              {shieldCooldown > 0 && <small>{Math.ceil(shieldCooldown)}</small>}
            </button>
            <button
              className="attack-button"
              onPointerDown={(event) => { event.stopPropagation(); attackPointerIdRef.current = event.pointerId; attackHeldRef.current = true; requestAttack(); }}
              disabled={orbs < 1 || gameOver}
              aria-label={t("launchAria")}
            >
              <span className="attack-orbit"><i /><i /></span>
              <small>FIRLAT</small>
            </button>
          </div>
        </>)}

        {!started && (
          <div className="modal-backdrop intro-backdrop" ref={modalRef} role="dialog" aria-modal="true" aria-label={t("startAria")}>
            <article className="intro-card glass-panel">
              {languageSwitch}
              <img className="intro-logo" src="/logo-224.jpg" alt="N Nova: Orbit Clash" width={224} height={224} />
              <h1>N NOVA<br /><em>ORBIT CLASH</em></h1>
              <p className="intro-copy">{t("tagline")}</p>
              <div className="mechanic-grid">
                <div><span className="mechanic-icon collect-icon">✦</span><b>{t("mechanicCollect")}</b><small>{t("mechanicCollectHint")}</small></div>
                <div><span className="mechanic-icon launch-icon">➤</span><b>{t("mechanicLaunch")}</b><small>{t("mechanicLaunchHint")}</small></div>
                <div><span className="mechanic-icon steal-icon">＋</span><b>{t("mechanicSteal")}</b><small>{t("mechanicStealHint")}</small></div>
                <div><span className="mechanic-icon shield-icon">⬡</span><b>{t("mechanicShield")}</b><small>{t("mechanicShieldHint")}</small></div>
              </div>
              {identity ? (
                <div className="intro-identity">
                  <span>{t("signedInAs", { name: identity.name })}</span>
                  <a href="/auth/logout">{t("signOut")}</a>
                </div>
              ) : (
                <label className="intro-nick">
                  <span>{t("nickLabel")}</span>
                  <input
                    value={nick}
                    onChange={(event) => { setNick(event.target.value); setSignInError(""); }}
                    onKeyDown={(event) => { if (event.key === "Enter") startOnline(); }}
                    maxLength={MAX_NICK_LENGTH}
                    placeholder="NOVA"
                    autoComplete="off"
                    spellCheck={false}
                    aria-label={t("nickAria")}
                  />
                </label>
              )}
              {signInError && <p className="intro-error">{t(signInError)}</p>}
              <button className="primary-button" onClick={() => startOnline()}><span>{t("play")}</span></button>
              {!identity && googleReady && (
                <a className="google-button" href="/auth/google">
                  <span className="google-mark" aria-hidden="true">G</span>{t("signInGoogle")}
                </a>
              )}
              <div className="intro-links">
                <button type="button" className="intro-link" onClick={openRooms}>{t("chooseRoom")}</button>
                <a className="intro-link" href="/leaderboard">{t("seeStandings")}</a>
                <a className="intro-link" href="/privacy">{t("privacy")}</a>
              </div>
              <small className="intro-note">{t("controlsHint")}</small>
            </article>
          </div>
        )}

        {paused && started && !gameOver && !helpOpen && !settingsOpen && (
          <div className="modal-backdrop" ref={modalRef} role="dialog" aria-modal="true" aria-label={t("pausedAria")}>
            <article className="pause-card glass-panel">
              <p className="eyebrow">{t("pausedEyebrow")}</p>
              <h2>{t("pausedTitle")}</h2>
              <button className="primary-button" onClick={() => setPaused(false)}><span>{t("resume")}</span></button>
            </article>
          </div>
        )}

        {gameOver && (
          <div className="modal-backdrop" ref={modalRef} role="dialog" aria-modal="true" aria-label={t("resultAria")}>
            <article className="result-card glass-panel">
              <div className="result-rank">#{rank}</div>
              <p className="eyebrow">{t("resultEyebrow")}</p>
              <h2>{rank === 1 ? t("resultChampion") : t("resultLost")}</h2>
              <div className="result-stats">
                <span><small>{t("score")}</small><b>{score.toLocaleString(locale)}</b></span>
                <span><small>{t("resultLastOrbs")}</small><b>{orbs}</b></span>
              </div>
              {placed?.unranked && <p className="result-placed">{t("placedAlone")}</p>}
              {placed && !placed.unranked && (
                <p className="result-placed">
                  {t("placedToday")} <b>#{placed.daily}</b>
                  {placed.weekly != null && <> · {t("placedWeek")} <b>#{placed.weekly}</b></>}
                  {placed.allTime != null && <> · {t("placedAllTime")} <b>#{placed.allTime}</b></>}
                  {placed.weekly == null && <span className="result-hint">{t("placedSignInHint")}</span>}
                </p>
              )}
              {online ? (
                <>
                  <button className="primary-button" onClick={leaveOnline}><span>{t("resultLeave")}</span></button>
                  <button className="secondary-button result-continue" onClick={continueOnline}>{t("resultContinue")}</button>
                  <a className="intro-standings" href="/leaderboard">{t("seeStandings")}</a>
                </>
              ) : (
                <button className="primary-button" onClick={resetGame}><span>{t("resultAgain")}</span></button>
              )}
            </article>
          </div>
        )}

        {helpOpen && (
          <div className="modal-backdrop" ref={modalRef} role="dialog" aria-modal="true" aria-label={t("helpAria")}>
            <article className="help-card glass-panel">
              <button className="close-button" onClick={closeOverlay} aria-label={t("close")}>×</button>
              <p className="eyebrow">{t("helpEyebrow")}</p>
              <h2>{t("helpRisk")}</h2>
              <div className="help-steps">
                <div><b>01</b><span><strong>{t("helpCollect")}</strong><small>{t("helpCollectCopy")}</small></span></div>
                <div><b>02</b><span><strong>{t("helpLaunch")}</strong><small>{t("helpLaunchCopy")}</small></span></div>
                <div><b>03</b><span><strong>{t("helpSteal")}</strong><small>{t("helpStealCopy")}</small></span></div>
                <div><b>04</b><span><strong>{t("helpShield")}</strong><small>{t("helpShieldCopy")}</small></span></div>
                <div><b>05</b><span><strong>{t("helpBoost")}</strong><small>{t("helpBoostCopy")}</small></span></div>
              </div>
              <button className="secondary-button" onClick={closeOverlay}>{t("helpClose")}</button>
              <button className="tutorial-restart" onClick={() => { tutorialStepRef.current = 0; setTutorialStep(0); closeOverlay(); }}>{t("helpReplayTutorial")}</button>
            </article>
          </div>
        )}

        {roomsOpen && (
          <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label={t("roomsAria")}>
            <article className="rooms-card glass-panel">
              <button className="close-button" onClick={() => setRoomsOpen(false)} aria-label={t("close")}>×</button>
              <h2>{t("roomsTitle")}</h2>
              <p className="rooms-hint">{t("roomsHint")}</p>

              <ul className="rooms-list scroll-thin">
                {rooms?.rooms.map((room) => {
                  const full = room.players >= rooms.capacity;
                  return (
                    <li key={room.id}>
                      <button disabled={full} onClick={() => { setRoomsOpen(false); startOnline(room.id); }}>
                        <b>{t("roomLabel", { number: room.number })}</b>
                        <span className="rooms-fill" aria-hidden="true">
                          <i style={{ width: `${Math.round((room.players / rooms.capacity) * 100)}%` }} />
                        </span>
                        <small>
                          {full ? t("roomsFull") : t("roomsPlayers", { count: room.players, capacity: rooms.capacity })}
                          {room.bots > 0 && ` · ${t("roomsBots", { count: room.bots })}`}
                        </small>
                      </button>
                    </li>
                  );
                })}
                {rooms?.rooms.length === 0 && <li className="rooms-empty">{t("roomsEmpty")}</li>}
              </ul>

              <button className="primary-button rooms-auto" onClick={() => { setRoomsOpen(false); startOnline(); }}>
                <span>{t("roomsAuto")}</span>
              </button>
            </article>
          </div>
        )}

        {settingsOpen && (
          <div className="modal-backdrop" ref={modalRef} role="dialog" aria-modal="true" aria-label={t("settingsAriaLabel")}>
            <article className="settings-card glass-panel">
              <button className="close-button" onClick={closeOverlay} aria-label={t("settingsClose")}>×</button>
              <p className="eyebrow">{t("settingsEyebrow")}</p>
              <h2>{t("settingsTitle")}</h2>
              <p className="settings-note">{t("settingsHint")}</p>
              <h3 className="settings-section-title">{t("settingsAppearance")}</h3>
              <div className="settings-grid">
                <label className="settings-color"><span>{t("settingsColor")} <b>{settings.playerColor}</b></span><input type="color" value={settings.playerColor} onChange={(event) => updateSetting("playerColor", event.target.value)} /></label>
                <label className="settings-select"><span>{t("settingsTheme")}</span><select value={settings.theme} onChange={(event) => updateSetting("theme", event.target.value as GameSettings["theme"])}>{THEME_OPTIONS.map((theme) => <option value={theme.value} key={theme.value}>{theme.label}</option>)}</select></label>
              </div>
              <p className="settings-note">
                {t("settingsNote")}
              </p>
              <div className="settings-actions">
                <button className="secondary-button" onClick={() => applySettings({ ...DEFAULT_GAME_SETTINGS })}>{t("settingsReset")}</button>
                <button className="primary-button" onClick={closeOverlay}><span>{t("settingsApply")}</span></button>
              </div>
            </article>
          </div>
        )}

        {started && !gameOver && !paused && !online && (
          <button className="pause-button glass-panel" onClick={() => setPaused(true)} aria-label={t("pauseAria")}>Ⅱ</button>
        )}
      </section>
    </main>
  );
}
